/* eslint-disable no-console, no-await-in-loop */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import './index.css';
import logoSrc from './assets/logo.png';
import HeaderBar from './components/HeaderBar';
import ConnectionForm from './components/ConnectionForm';
import FileList from './components/FileList';
import EditorModal from './components/EditorModal';
import InputModal from './components/InputModal';
import CodeEditor from './components/CodeEditor';
import LargeFileViewer from './components/LargeFileViewer';
import NbtEditorModal from './components/NbtEditorModal';
import ConfigEditor from './components/ConfigEditor';
import ActivityPanel from './components/ActivityPanel';
import MobilePanelBar from './components/MobilePanelBar';
import FolderSizeConfirmModal from './components/FolderSizeConfirmModal';
import NewLoginModal from './components/NewLoginModal';
import SaveLoginModal from './components/SaveLoginModal';
import SavedLoginsModal from './components/SavedLoginsModal';
import {
  joinRemotePath,
  remoteParent,
  joinLocalPath,
  assertSafeLocalEntryName,
  localParent,
  formatSize,
  truncatePath } from
'./utils/paths';
import { createConnection, matchesDeepLinkConnection, parseHostInput } from './utils/connection';
import { DEFAULT_SETTINGS } from './utils/settings';
import { getConfigEditor } from './utils/configEditors';
import { cloneListingResult, filterListingResult, normalizeListingResult } from './utils/listings';
import { RiEyeLine, RiEyeOffLine, RiFolderOpenLine, RiRefreshLine, RiArrowLeftLine, RiArrowRightLine, RiFileAddLine, RiFolderAddLine, RiDownload2Line, RiUpload2Line, RiUploadCloud2Line, RiCheckboxMultipleLine, RiLoader4Line, RiMore2Line } from 'react-icons/ri';
import {
  APP_VERSION,
  UPDATE_ENDPOINT,
  compareVersions,
  normalizeTrustedUpdateDownloadUrl } from
'./utils/updates';
import { WEB_UPLOAD_CONCURRENCY } from '../shared/uploadTuning.js';


import SettingsPanel from './components/SettingsPanel';
import TransferView from './components/TransferView';
import ErrorBoundary from './components/ErrorBoundary';
import TabBar from './components/TabBar';
import BuilderSherman from './components/BuilderSherman';

import { useSettings } from './hooks/useSettings';
import { useLogs } from './hooks/useLogs';
import { useFileOperations } from './hooks/useFileOperations';
import { useConnection } from './hooks/useConnection';
import { useTransferQueue } from './hooks/useTransferQueue';
import { useEditors } from './hooks/useEditors';
import { useTheme } from './hooks/useTheme';
import { useTabs } from './hooks/useTabs';

const createEmptyTransferRightState = () => ({
  mode: 'remote',
  localPath: '',
  config: { host: '', port: 2022, username: '', password: '', protocol: 'sftp' }
});

function App() {
  const initialViewMode = 'edit';
  const [api, setApi] = useState(() => typeof window !== 'undefined' && window.api ? window.api : null);
  const [message, setMessage] = useState('');
  const [modal, setModal] = useState(null);
  const [saveModal, setSaveModal] = useState(null);
  const [zipInProgress, setZipInProgress] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [folderSizeMap, setFolderSizeMap] = useState({}); // path → calculated size
  const [folderSizeLoading, setFolderSizeLoading] = useState(new Set()); // paths currently calculating
  const [folderSizeConfirm, setFolderSizeConfirm] = useState(null); // { path, name } for confirmation modal
  const paneContainerRef = useRef(null);
  const uploadFilesInputRef = useRef(null);
  const uploadFolderInputRef = useRef(null);
  const wakeLockRef = useRef(null);
  const windowDragCounter = useRef(0);
  const [windowDragOver, setWindowDragOver] = useState(false);
  const connectionRef = useRef(null);
  const connectedRef = useRef(false);
  const pendingDeepLinkRef = useRef(null);
  const webConnectLinkHandledRef = useRef(false);
  const tabSnapshotsRef = useRef({});
  const transferLeftBindingKeyRef = useRef(null);

  const {
    tabs,
    activeTabId,
    setActiveTabId,
    addTab,
    removeTab,
    setTabLabel,
    setTabTooltip,
    setTabConnected,
  } = useTabs();
  const [paneSplit, setPaneSplit] = useState(0.35);
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [transferTab, setTransferTab] = useState('actions');

  const [updateStatus, setUpdateStatus] = useState('idle');
  const [latestUpdate, setLatestUpdate] = useState(null);
  const [updateError, setUpdateError] = useState('');
  const [updateCheckedAt, setUpdateCheckedAt] = useState(null);
  const [updateModalSeenVersion, setUpdateModalSeenVersion] = useState(null);


  const [transferSplit, setTransferSplit] = useState(0.5);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [showPanelFiles, setShowPanelFiles] = useState(false);
  const [panelFileWarning, setPanelFileWarning] = useState(null);
  const [tabSwitchingOverlay, setTabSwitchingOverlay] = useState(false);
  const [deletingPaths, setDeletingPaths] = useState(new Set());
  const [activityHeight, setActivityHeight] = useState(240);
  const [mobilePanelTab, setMobilePanelTab] = useState('remote');
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [collapseActionBar, setCollapseActionBar] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1467);
  const [isMobileLayout, setIsMobileLayout] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const [webCapabilities, setWebCapabilities] = useState(null);
  const [transferLeft, setTransferLeft] = useState({ host: '', port: 2022, username: '', password: '', protocol: 'sftp' });
  const [transferRight, setTransferRight] = useState(createEmptyTransferRightState);
  const [transferLeftPath, setTransferLeftPath] = useState('/');
  const [transferRightPath, setTransferRightPath] = useState('/');
  const [transferLeftEntries, setTransferLeftEntries] = useState([]);
  const [transferRightEntries, setTransferRightEntries] = useState([]);
  const [transferLeftSelected, setTransferLeftSelected] = useState(new Set());
  const [transferRightSelected, setTransferRightSelected] = useState(new Set());
  const [transferConnecting, setTransferConnecting] = useState({ left: false, right: false });
  const [transferLoadingPath, setTransferLoadingPath] = useState({ left: null, right: null });
  const [transferRefreshing, setTransferRefreshing] = useState({ left: false, right: false });
  const [transferConnected, setTransferConnected] = useState({ left: false, right: false });
  const [transferStatusMap, setTransferStatusMap] = useState({});
  const [transferring, setTransferring] = useState(false);
  const [transferOverlaySide, setTransferOverlaySide] = useState(null);


  const [paneSplitEdit, setPaneSplitEdit] = useState(0.3);
  const [editorLanguage, setEditorLanguage] = useState('auto');
  const [editorWrap, setEditorWrap] = useState(true);
  const [newLoginOpen, setNewLoginOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loadingSavedLogin, setLoadingSavedLogin] = useState(true);
  const [startupPicker, setStartupPicker] = useState(null);
  const [startupPickerSelected, setStartupPickerSelected] = useState(new Set());
  const [openingLoginsStatus, setOpeningLoginsStatus] = useState(null);
  const [connectingLoginName, setConnectingLoginName] = useState(null);
  const [webTransferMode, setWebTransferMode] = useState(false);
  const [infoPage, setInfoPage] = useState(0);
  useEffect(() => { setInfoPage(0); }, [webTransferMode]);

  const [loginDraft, setLoginDraft] = useState({ host: '', port: 2022, username: '', password: '', protocol: 'sftp' });


  const { settings, updateSetting, resetSettings, setSettings } = useSettings();
  const { logs, addLog, setLogs } = useLogs(settings);

  const {
    theme, themeDraft, handleThemeChange, handleThemeCommit, handleThemeReset
  } = useTheme();

  const {
    localPath, setLocalPath,
    remotePath, setRemotePath,
    localEntries, remoteEntries, setRemoteEntries,
    selectedLocal, setSelectedLocal,
    selectedRemote, setSelectedRemote,
    localSelectedSet, setLocalSelectedSet,
    remoteSelectedSet, setRemoteSelectedSet,
    loadingRemotePath, loadingRemote, loadingLocal,
    refreshLocal, refreshRemote,
    handleCreateLocalFile, handleCreateLocalFolder, handleCreateRemoteFolder,
    handleDeleteLocal, handleDeleteRemote
  } = useFileOperations(api, settings, addLog);

  const {
    connection, setConnection,
    connected, setConnected, connecting, setConnecting,
    connectWithConfig, disconnect,
    savedLogins, setSavedLogins, savedLoginsOpen, setSavedLoginsOpen,
    savedLoginsTab, setSavedLoginsTab,
    vaultPassword, setVaultPassword,
    vaultUnlocking, vaultError, setVaultError, vaultStatus, setVaultStatus,
    handleVaultUnlock, saveLogin, handleDeleteLogin,
    connectSavedLogin
  } = useConnection({
    api, settings, refreshLocal, refreshRemote, addLog, setMessage, activeTabId
  });

  const {
    transfers,
    startTransfer,
    updateTransfer,
    cancelTransfer,
    cancelAllActive,
    clearFinished

  } = useTransferQueue();

  const activeTransferCount = useMemo(
    () =>
    transfers.filter(
      (entry) =>
      (entry.type === 'upload' || entry.type === 'download') &&
      entry.status === 'in-progress'
    ).length,
    [transfers]
  );

  const {
    editor, setEditor,
    nbtEditor, setNbtEditor,
    editorView, setEditorView,
    configEditor, setConfigEditor,
    loadingFilePath, openEditor: _openEditor, loadLargeFileChunk, saveEditor,
    openNbtEditor, saveNbtEditor,
    loadNbtChunk
  } = useEditors({
    api, startTransfer, refreshLocal, refreshRemote,
    localPath, remotePath, connection, addLog, setMessage
  });

  const prevConnectionKeyRef = useRef(null);
  useEffect(() => {
    const key = connected ? `${connection?.host}:${connection?.port}:${connection?.username}` : null;
    if (key && prevConnectionKeyRef.current && key !== prevConnectionKeyRef.current) {
      setEditor(null);
      setNbtEditor(null);
      setConfigEditor(null);
    }
    prevConnectionKeyRef.current = key;
  }, [connection?.host, connection?.port, connection?.username, connected]);

  // Wraps openEditor so mobile auto-switches to the editor panel after opening
  const openEditor = useCallback(async (...args) => {
    await _openEditor(...args);
    if (isMobileLayout) setMobilePanelTab('editor');
  }, [_openEditor, isMobileLayout]);

  const switchToTab = useCallback(async (newTabId, snapshotOverride = null, fromTabId = null) => {
    const saveToId = fromTabId ?? activeTabId;
    if (newTabId === saveToId) return;
    setTabSwitchingOverlay(true);
    try {
      tabSnapshotsRef.current[saveToId] = snapshotOverride ?? {
        connection,
        connected,
        remotePath,
        remoteEntries: cloneListingResult(remoteEntries),
        transferRight,
        transferRightPath,
        transferRightEntries: cloneListingResult(transferRightEntries),
        transferRightConnected: transferConnected.right,
        message,
        editor,
        nbtEditor,
        configEditor,
      };
      if (api?.setActiveTab) await api.setActiveTab(newTabId).catch(() => {});
      const saved = tabSnapshotsRef.current[newTabId];
      if (saved) {
        setConnection(saved.connection);
        setConnected(saved.connected);
        setConnecting(false);
        setRemotePath(saved.remotePath);
        setRemoteEntries(saved.remoteEntries);
        setTransferRight(saved.transferRight ?? createEmptyTransferRightState());
        setTransferRightPath(saved.transferRightPath || '/');
        setTransferRightEntries(saved.transferRightEntries ?? []);
        setTransferRightSelected(new Set());
        setTransferConnected((prev) => ({
          ...prev,
          right: Boolean(saved.transferRightConnected)
        }));
        setMessage(saved.message || '');
        setEditor(saved.editor ?? null);
        setNbtEditor(saved.nbtEditor ?? null);
        setConfigEditor(saved.configEditor ?? null);
        setActiveTabId(newTabId);
        if (saved.connected) {
          await refreshRemote(saved.remotePath).catch(() => {});
        }
      } else {
        setConnection(createConnection());
        setConnected(false);
        setConnecting(false);
        setRemotePath('/');
        setRemoteEntries([]);
        setTransferRight(createEmptyTransferRightState());
        setTransferRightPath('/');
        setTransferRightEntries([]);
        setTransferRightSelected(new Set());
        setTransferConnected((prev) => ({ ...prev, right: false }));
        setMessage('');
        setEditor(null);
        setNbtEditor(null);
        setConfigEditor(null);
        setActiveTabId(newTabId);
      }
    } finally {
      setTabSwitchingOverlay(false);
    }
  }, [
    activeTabId, api, connection, connected, remotePath, remoteEntries,
    transferRight, transferRightPath, transferRightEntries, transferConnected.right, message,
    editor, nbtEditor, configEditor,
    setConnection, setConnected, setConnecting, setRemotePath, setRemoteEntries,
    setMessage, setEditor, setNbtEditor, setConfigEditor, refreshRemote, setActiveTabId,
  ]);

  const handleAddTab = useCallback(() => {
    const newId = addTab();
    switchToTab(newId);
  }, [addTab, switchToTab]);

  const createConnectedTabSnapshot = useCallback((cfg) => ({
    connection: cfg,
    connected: true,
    remotePath: '/',
    remoteEntries: [],
    transferRight: createEmptyTransferRightState(),
    transferRightPath: '/',
    transferRightEntries: [],
    transferRightConnected: false,
    message: '',
    editor: null,
    nbtEditor: null,
    configEditor: null,
  }), []);

  const openLoginsInTabs = useCallback(async (entries) => {
    setStartupPicker(null);
    if (entries.length === 0) return;
    const total = entries.length;
    const [first, ...rest] = entries;
    setOpeningLoginsStatus(`Connecting to ${first.name}… (1 of ${total})`);
    const firstCfg = await connectSavedLogin(first);
    if (!firstCfg) {
      setOpeningLoginsStatus(null);
      return;
    }
    let currentTabId = activeTabId;
    let currentSnapshot = createConnectedTabSnapshot(firstCfg);
    tabSnapshotsRef.current[currentTabId] = currentSnapshot;
    let i = 2;
    for (const entry of rest) {
      setOpeningLoginsStatus(`Connecting to ${entry.name}… (${i} of ${total})`);
      const newId = addTab();
      await switchToTab(newId, currentSnapshot, currentTabId);
      const cfg = await connectSavedLogin(entry, newId);
      currentTabId = newId;
      currentSnapshot = cfg ? createConnectedTabSnapshot(cfg) : {
        connection: createConnection(),
        connected: false,
        remotePath: '/',
        remoteEntries: [],
        transferRight: createEmptyTransferRightState(),
        transferRightPath: '/',
        transferRightEntries: [],
        transferRightConnected: false,
        message: '',
        editor: null,
        nbtEditor: null,
        configEditor: null,
      };
      tabSnapshotsRef.current[currentTabId] = currentSnapshot;
      i++;
    }
    setOpeningLoginsStatus(null);
  }, [addTab, switchToTab, connectSavedLogin, activeTabId, createConnectedTabSnapshot]);

  const handleCloseTab = useCallback(async (id) => {
    if (api?.disconnect) await api.disconnect(id).catch(() => {});
    setTabConnected(id, false);
    if (id === activeTabId) {
      const idx = tabs.findIndex((t) => t.id === id);
      const next = tabs[idx + 1] || tabs[idx - 1];
      if (next) await switchToTab(next.id);
    }
    removeTab(id);
    delete tabSnapshotsRef.current[id];
  }, [api, activeTabId, tabs, switchToTab, removeTab, setTabConnected]);

  useEffect(() => {
    setTabLabel(activeTabId, connected ? (connection?.host || null) : null);
    setTabTooltip(
      activeTabId,
      connected ?
        [connection?.username, connection?.host].filter(Boolean).join('@') || connection?.host || null :
        null
    );
    setTabConnected(activeTabId, connected);
  }, [activeTabId, connected, connection?.host, connection?.username, setTabLabel, setTabTooltip, setTabConnected]);

  const isWebMode = api?.mode === 'web';
  const capabilities = isWebMode ?
  {
    ...(api?.capabilities || {}),
    archive: false,
    ...(webCapabilities || {})
  } :
  api?.capabilities || {};
  const KINETIC_DOMAINS = ['kinetichosting.net', 'kineticpanel.net', 'kinetichosting.gg', 'kinetichosting.com', 'kineticpanel.com'];
  const isKineticHost = (host) => {
    if (!host) return true;
    const lower = host.toLowerCase();
    return KINETIC_DOMAINS.some((d) => lower === d || lower.endsWith('.' + d));
  };
  const canUseWindowControls = capabilities.windowControls !== false;
  const canUseVault = capabilities.vault !== false;
  const canUseArchive = capabilities.archive !== false;
  const canUseNbt = capabilities.nbt !== false;
  const canUseTransfer = capabilities.transfer !== false;
  const canUseLocalFs = capabilities.localFs !== false;
  const localFsUnavailableMessage =
  capabilities.localFsReason || 'Browser local filesystem is not available.';
  const localFolderHint =
  'Chrome may block the top-level Downloads folder, so pick or create a subfolder inside Downloads.';
  const archiveUnavailableMessage =
  capabilities.archiveReason || 'Archive features are disabled in web mode.';
  const vaultUnavailableMessage =
  capabilities.vaultReason || 'Encrypted local login storage is not available in this browser.';
  const canPickLocalFolder = typeof api?.pickLocalDirectory === 'function';
  const canBrowserUpload = typeof api?.uploadBrowserFile === 'function';
  const hasPickedLocalFolder = !canPickLocalFolder || Boolean(localPath && localPath !== 'Browser');
  const showWebLoginScreen = isWebMode && !connected;
  const showDesktopLoginScreen = !isWebMode && !connected;
  const isTransferProtectionActive = isWebMode && activeTransferCount > 0;
  const viewTabs = useMemo(() => ['edit', 'transfer', 'settings'], []);

  useEffect(() => {
    if (!isWebMode || typeof api?.getSessionStatus !== 'function') {
      setWebCapabilities(null);
      return;
    }
    let cancelled = false;
    api.getSessionStatus().then((status) => {
      if (cancelled) return;
      const nextCapabilities =
      status?.capabilities && typeof status.capabilities === 'object' ?
      status.capabilities :
      {};
      setWebCapabilities(nextCapabilities);
    }).catch(() => {
      if (!cancelled) {
        setWebCapabilities((prev) => prev || {});
      }
    });
    return () => {
      cancelled = true;
    };
  }, [api, isWebMode]);

  useEffect(() => {
    if (viewMode === 'transfer' && !canUseTransfer) {
      setViewMode('edit');
    }
  }, [canUseTransfer, viewMode]);

  useEffect(() => {
    if (!isWebMode || !canUseLocalFs || !canPickLocalFolder || hasPickedLocalFolder) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const home = await api.getHomeDir();
        if (cancelled || !home || home === 'Browser') return;
        await refreshLocal(home);
      } catch {
        // Ignore restore failures and fall back to manual picking.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, canPickLocalFolder, canUseLocalFs, hasPickedLocalFolder, isWebMode, refreshLocal]);

  useEffect(() => {
    if (viewMode === 'normal') {
      setViewMode('edit');
    }
  }, [viewMode]);

  const releaseWakeLock = useCallback(async () => {
    const lock = wakeLockRef.current;
    if (!lock) return;
    try {
      await lock.release();
    } catch {
      // Ignore release failures from stale locks.
    } finally {
      wakeLockRef.current = null;
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!isTransferProtectionActive) return;
    if (typeof navigator === 'undefined') return;
    if (wakeLockRef.current) return;
    if (!navigator.wakeLock?.request) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener?.('release', () => {
        wakeLockRef.current = null;
      });
    } catch {
      // Wake lock can be denied by browser policy or low battery.
    }
  }, [isTransferProtectionActive]);

  useEffect(() => {
    if (!isWebMode) return undefined;
    if (!isTransferProtectionActive) {
      void releaseWakeLock();
      return undefined;
    }
    void requestWakeLock();
    return () => {
      void releaseWakeLock();
    };
  }, [isWebMode, isTransferProtectionActive, requestWakeLock, releaseWakeLock]);

  useEffect(() => {
    if (!isWebMode) return undefined;
    if (!isTransferProtectionActive) return undefined;
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isWebMode, isTransferProtectionActive, requestWakeLock]);

  useEffect(() => {
    if (!isTransferProtectionActive) return undefined;
    if (typeof window === 'undefined') return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTransferProtectionActive]);

  const transferProgress = null;
  const disconnectBlocked = transferring || zipInProgress || activeTransferCount > 0;
  const disconnectBlockedMessage = 'Wait for the current transfer to finish before disconnecting.';

  const updateLabel = latestUpdate?.version_number ?
  `v${latestUpdate.version_number}` :
  latestUpdate?.name;
  const updateModalKey = latestUpdate?.version_number || latestUpdate?.name;
  const updateAvailable =
  latestUpdate?.version_number &&
  compareVersions(latestUpdate.version_number, APP_VERSION) > 0;
  const trustedUpdateDownloadUrl = useMemo(() => {
    if (!latestUpdate?.download_url) return '';
    try {
      return normalizeTrustedUpdateDownloadUrl(latestUpdate.download_url);
    } catch {
      return '';
    }
  }, [latestUpdate?.download_url]);
  const updateDownloadBlocked =
  updateAvailable &&
  Boolean(latestUpdate?.download_url) &&
  !trustedUpdateDownloadUrl;
  const canDownloadUpdate = updateAvailable && Boolean(trustedUpdateDownloadUrl);

  const handleDownloadUpdate = useCallback(async () => {
    const url = trustedUpdateDownloadUrl;
    if (!url) return;
    try {
      if (api?.openExternal) {
        await api.openExternal(url);
        if (api?.closeWindow) api.closeWindow();
        return;
      }
      if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener');
        if (api?.closeWindow) {
          api.closeWindow();
        } else {
          window.close();
        }
      }
    } catch (err) {
      setMessage(err?.message || 'Failed to open update download.');
    }
  }, [api, trustedUpdateDownloadUrl]);

  const checkForUpdates = useCallback(async (signal) => {
    setUpdateStatus('checking');
    setUpdateError('');
    try {
      const response = await fetch(UPDATE_ENDPOINT, { signal });
      if (!response.ok) {
        throw new Error(`Update check failed (${response.status})`);
      }
      const payload = await response.json();
      if (signal?.aborted) return;
      const latest = Array.isArray(payload) ? payload[0] : payload;
      setLatestUpdate(latest || null);
      setUpdateStatus('ready');
      setUpdateCheckedAt(Date.now());
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setUpdateStatus('error');
      setUpdateError(err?.message || 'Update check failed');
      setUpdateCheckedAt(Date.now());
    }
  }, []);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 10000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    let attempts = 0;
    const timer = setInterval(() => {
      if (typeof window !== 'undefined' && window.api) {
        setApi(window.api);
        clearInterval(timer);
      } else {
        attempts += 1;
        if (attempts > 10) {
          setMessage('API bridge unavailable. Preload may have failed to load.');
          clearInterval(timer);
        }
      }
    }, 150);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleError = (event) => {
      const message = event?.error?.message || event?.message || 'Unexpected error.';
      setMessage(message);

      console.error('Window error:', event?.error || event);
    };
    const handleRejection = (event) => {
      const reason = event?.reason;
      const message = reason?.message || String(reason || 'Unhandled rejection.');
      setMessage(message);

      console.error('Unhandled rejection:', reason);
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void checkForUpdates(controller.signal);
    return () => controller.abort();
  }, [checkForUpdates]);

  useEffect(() => {
    if (!canDownloadUpdate) return;
    if (!updateModalKey) return;
    if (updateModalSeenVersion === updateModalKey) return;
    if (modal) return;
    const sizeText = latestUpdate?.size_bytes ? ` (${formatSize(latestUpdate.size_bytes)})` : '';
    const versionLabel = latestUpdate?.version_number ?
    `v${latestUpdate.version_number}` :
    updateModalKey;
    const modalKey = updateModalKey;
    setModal({
      title: 'Update available',
      description: `Version ${versionLabel} is available${sizeText}. Download now? The app will close after starting the download.`,
      requireInput: false,
      confirmLabel: 'Download',
      cancelLabel: 'Later',
      onConfirm: async () => {
        setModal(null);
        setUpdateModalSeenVersion(modalKey);
        await handleDownloadUpdate();
      },
      onCancel: () => {
        setModal(null);
        setUpdateModalSeenVersion(modalKey);
      }
    });
  }, [
  canDownloadUpdate,
  updateModalKey,
  updateModalSeenVersion,
  modal,
  latestUpdate?.size_bytes,
  latestUpdate?.version_number,
  handleDownloadUpdate]
  );

  useEffect(() => {
    connectionRef.current = connection;
  }, [connection]);

  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);

  useEffect(() => {
    if (!editor) {
      setConfigEditor(null);
      setEditorView('raw');
      return;
    }
    if (editor.isLargeFile || editor.isImage) {
      setConfigEditor(null);
      setEditorView('raw');
      return;
    }
    const active = getConfigEditor(editor);
    setConfigEditor(active || null);
    setEditorView(active ? 'config' : 'raw');
  }, [editor?.isImage, editor?.isLargeFile, editor?.path, setConfigEditor]);

  useEffect(() => {
    const limit = settings.logging?.retention ?? DEFAULT_SETTINGS.logging.retention;
    setLogs((prev) => prev.slice(0, Math.max(0, limit)));
  }, [settings.logging?.retention]);

  const buildDraftFromPayload = (payload) => ({
    host: payload?.host || '',
    port: payload?.port ? Number(payload.port) : payload?.protocol === 'ftps' ? 21 : 2022,
    username: payload?.username || payload?.user || '',
    password: payload?.password || payload?.pass || '',
    protocol: payload?.protocol || 'sftp'
  });

  const toPositivePort = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  const parseWebConnectPayload = () => {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const rawPath = url.pathname.replace(/\/+$/, '').toLowerCase();
    const action = String(params.get('action') || '').toLowerCase();
    const looksLikeConnect =
    rawPath.endsWith('/connect') ||
    action === 'connect' ||
    params.has('host') ||
    params.has('ip') ||
    params.has('server');
    if (!looksLikeConnect) return null;

    const hostInput = params.get('host') || params.get('ip') || params.get('server') || '';
    const protocolInput = params.get('protocol') || params.get('scheme') || 'sftp';
    const parsedHost = parseHostInput(hostInput, protocolInput);
    const host = parsedHost.host || String(hostInput || '').trim();
    const payload = {
      action: 'connect',
      host,
      port: toPositivePort(params.get('port')) ?? parsedHost.port,
      username: params.get('username') || params.get('user') || '',
      password: '',
      protocol: parsedHost.protocol || protocolInput || 'sftp',
      path: params.get('path') || params.get('open') || ''
    };
    const hasAnyData =
    payload.host || payload.username || payload.password || payload.path;
    return hasAnyData ? payload : null;
  };

  const consumeWebConnectParams = () => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const keys = [
    'action',
    'host',
    'ip',
    'server',
    'port',
    'username',
    'user',
    'password',
    'pass',
    'protocol',
    'scheme',
    'path',
    'open'];
    const hasKnownKey = keys.some((key) => url.searchParams.has(key));
    const rawPath = url.pathname.replace(/\/+$/, '');
    const isConnectPath = rawPath.toLowerCase().endsWith('/connect');
    if (!hasKnownKey && !isConnectPath) return;

    keys.forEach((key) => url.searchParams.delete(key));
    if (isConnectPath) {
      const parentPath = rawPath.slice(0, -'/connect'.length);
      url.pathname = parentPath || '/';
    }
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) {
      window.history.replaceState({}, document.title, nextUrl);
    }
  };

  const openDeepLinkPath = async (targetPath) => {
    if (!targetPath) return;
    const parent = remoteParent(targetPath);
    const nextPath = parent || '/';
    setRemotePath(nextPath);
    await refreshRemote(nextPath, { force: true });
    await openEditor(
      { path: targetPath, name: targetPath.split('/').pop(), type: 'file' },
      true
    );
  };

  const openNewLoginWithPayload = (payload) => {
    setLoginDraft(payload ? buildDraftFromPayload(payload) : createConnection());
    setShowPassword(false);
    setSavedLoginsOpen(false);
    setNewLoginOpen(true);
  };

  const handleDeepLinkPayload = (payload) => {
    if (payload?.action !== 'connect') return;
    const cfg = {
      host: payload.host || '',
      port: payload.port || (payload.protocol === 'ftps' ? 21 : 2022),
      username: payload.username || '',
      password: payload.password || '',
      protocol: payload.protocol || 'sftp'
    };
    const alreadyConnected =
    connectedRef.current && matchesDeepLinkConnection(payload, connectionRef.current);
    if (alreadyConnected) {
      if (payload.path) {
        void openDeepLinkPath(payload.path);
      }
      return;
    }
    if (!connectedRef.current) {
      setLoginDraft(buildDraftFromPayload(payload));
    }
    const hasCredentials = cfg.host && cfg.username && cfg.password;
    pendingDeepLinkRef.current = payload;
    if (!connectedRef.current && !hasCredentials) {
      if (isWebMode) return;
      void promptLoginForDeepLink(payload);
      return;
    }
    setConnection(cfg);
    connectWithConfig(cfg, payload).then((ok) => {
      if (ok) {
        void openPendingDeepLink(cfg);
        return;
      }
      if (!connectedRef.current) {
        void promptLoginForDeepLink(payload);
      }
    });
  };

  useEffect(() => {
    if (!api || !api.onDeepLink) return undefined;
    const unsubscribe = api.onDeepLink((payload) => handleDeepLinkPayload(payload));
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [api]);

  useEffect(() => {
    if (!isWebMode || !api) {
      setLoadingSavedLogin(false);
      return;
    }
    if (webConnectLinkHandledRef.current) return;
    webConnectLinkHandledRef.current = true;
    const url = new URL(window.location.href);
    const isTransfer =
      url.searchParams.get('transfer') === 'true' ||
      url.searchParams.get('action') === 'transfer';
    if (isTransfer) {
      setWebTransferMode(true);
      url.searchParams.delete('transfer');
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (nextUrl !== currentUrl) {
        window.history.replaceState({}, document.title, nextUrl);
      }
    }
    const payload = parseWebConnectPayload();
    if (payload) {
      consumeWebConnectParams();
      handleDeepLinkPayload(payload);
      setLoadingSavedLogin(false);
      return;
    }
    (async () => {
      setLoadingSavedLogin(true);
      try {
        const list = await api.vaultList();
        setSavedLogins(list);
        if (list.length === 0) return;
        if (list.length === 1) {
          await connectSavedLogin(list[0]);
          return;
        }
        // Multiple saved logins — show picker
        setStartupPicker(list);
        setStartupPickerSelected(new Set([list[0].name]));
      } catch {
      } finally {
        setLoadingSavedLogin(false);
      }
    })();
  }, [api, isWebMode]);

  const listTransferLocal = async (targetPath) => {
    const fallback = targetPath || (await api.getHomeDir());
    const list = await api.listLocal(fallback);
    return {
      base: list?.base ?? fallback,
      items: Array.isArray(list?.items) ? list.items : []
    };
  };

  const handleTransferConnect = async (side = 'right', configOverride = null, pathOverride = null) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return false;
    }
    const targetSide = side === 'left' ? 'left' : 'right';

    // Block connecting the transfer tool to the same server already connected
    if (targetSide === 'right' && transferRight.mode !== 'local' && connection) {
      const cfg = configOverride || transferRight.config;
      if (cfg.host && cfg.host === connection.host &&
          String(cfg.port) === String(connection.port) &&
          cfg.username === connection.username) {
        setMessage('You cannot connect the transfer tool to the same server you are already connected to. Please use a different server.');
        return false;
      }
    }

    const connectCfg = configOverride || (targetSide === 'left' ? connection : transferRight.config);
    if (!connectCfg?.password) {
      setMessage('Please enter a password before connecting.');
      return false;
    }

    setTransferConnecting((prev) => ({ ...prev, [targetSide]: true }));
    try {
      if (targetSide === 'right' && transferRight.mode === 'local') {
        const { base, items } = await listTransferLocal(transferRight.localPath);
        setTransferRightPath(base);
        setTransferRightEntries(items);
        setTransferConnected((prev) => ({ ...prev, right: true }));
        setTransferRight((prev) => ({ ...prev, localPath: base }));
        return true;
      }

      const config = normalizeConfig(
        configOverride || (targetSide === 'left' ? connection : transferRight.config)
      );
      await api.transferConnect(targetSide, config);
      const path =
        pathOverride ||
        (targetSide === 'left' ? transferLeftPath || remotePath || '/' : transferRightPath || '/');
      const list = normalizeListingResult(await api.transferList(targetSide, path));
      if (targetSide === 'left') {
        setTransferLeftEntries(list || []);
        setTransferLeftPath(path);
      } else {
        setTransferRightEntries(list || []);
        setTransferRightPath(path);
      }
      setTransferConnected((prev) => ({ ...prev, [targetSide]: true }));
      return true;
    } catch (err) {
      setMessage(err?.message || 'Transfer connect failed.');
      setTransferConnected((prev) => ({ ...prev, [targetSide]: false }));
      return false;
    } finally {
      setTransferConnecting((prev) => ({ ...prev, [targetSide]: false }));
    }
  };

  const handleTransferDisconnect = () => {
    if (disconnectBlocked) {
      setMessage(disconnectBlockedMessage);
      return;
    }
    setTransferConnected((prev) => ({ ...prev, right: false }));
    setTransferRightEntries([]);
    setTransferRightPath('/');
    setTransferRightSelected(new Set());
    setTransferRight({ mode: 'remote', localPath: '', config: { host: '', port: 2022, username: '', password: '', protocol: 'sftp' } });
  };

  const fetchTransferListingPage = async (side, path, page) =>
    normalizeListingResult(await api.transferList(side, path, { page }));

  const fetchAllTransferEntries = async (side, path) => {
    const all = [];
    let currentPage = 1;
    while (true) {
      const page = await fetchTransferListingPage(side, path, currentPage);
      all.push(...page);
      if (currentPage >= (page.pageCount || 1)) break;
      currentPage += 1;
    }
    return all;
  };

  const refreshTransferSide = async (side) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    const targetSide = side === 'left' ? 'left' : 'right';
    setTransferRefreshing((prev) => ({ ...prev, [targetSide]: true }));
    try {
      if (targetSide === 'right' && transferRight.mode === 'local') {
        const { base, items } = await listTransferLocal(transferRight.localPath || transferRightPath);
        setTransferRightPath(base);
        setTransferRightEntries(items);
        setTransferRight((prev) => ({ ...prev, localPath: base }));
        return;
      }
      const path = targetSide === 'left' ? transferLeftPath || '/' : transferRightPath || '/';
      const currentEntries = targetSide === 'left' ? transferLeftEntries : transferRightEntries;
      const list = await fetchTransferListingPage(targetSide, path, Number(currentEntries?.page) || 1);
      if (targetSide === 'left') {
        setTransferLeftEntries(list || []);
      } else {
        setTransferRightEntries(list || []);
      }
    } catch (err) {
      setMessage(err?.message || 'Refresh failed.');
    } finally {
      setTransferRefreshing((prev) => ({ ...prev, [targetSide]: false }));
    }
  };

  const navigateTransferSide = async (side, targetPath, options = {}) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    const targetSide = side === 'left' ? 'left' : 'right';
    const nextPath =
    targetPath || (targetSide === 'left' ? transferLeftPath : transferRightPath) || '/';
    const currentPath = targetSide === 'left' ? transferLeftPath : transferRightPath;
    const currentEntries = targetSide === 'left' ? transferLeftEntries : transferRightEntries;
    const requestedPage =
      Number.isInteger(options?.page) && options.page > 0 ?
        options.page :
        nextPath === currentPath ?
          Number(currentEntries?.page) || 1 :
          1;
    setTransferLoadingPath((prev) => ({ ...prev, [targetSide]: nextPath }));
    try {
      if (targetSide === 'right' && transferRight.mode === 'local') {
        const { base, items } = await listTransferLocal(nextPath || transferRight.localPath);
        setTransferRightPath(base);
        setTransferRightEntries(items);
        setTransferRight((prev) => ({ ...prev, localPath: base }));
        setTransferConnected((prev) => ({ ...prev, right: true }));
        return;
      }
      const list = await fetchTransferListingPage(targetSide, nextPath, requestedPage);
      if (targetSide === 'left') {
        setTransferLeftEntries(list || []);
        setTransferLeftPath(nextPath);
      } else {
        setTransferRightEntries(list || []);
        setTransferRightPath(nextPath);
      }
    } catch (err) {
      setMessage(err?.message || 'Navigation failed.');
    } finally {
      setTransferLoadingPath((prev) => ({ ...prev, [targetSide]: null }));
    }
  };

  const expandEntry = async (side, entry, targetDir) => {
    if (entry.type !== 'dir') {
      return [{ path: entry.path, name: entry.name, targetDir }];
    }
    const children = await fetchAllTransferEntries(side, entry.path).catch(() => []);
    const subTarget = `${targetDir}/${entry.name}`.replace('//', '/');
    const results = [];
    for (const child of children) {
      results.push(...await expandEntry(side, child, subTarget));
    }
    return results.length > 0 ? results : [{ path: entry.path, name: entry.name, targetDir, isDir: true }];
  };

  const runTransferBatch = async (fromSide, toSide, sourceEntries, baseTargetDir) => {
    if (!api) { setMessage('API bridge unavailable.'); return; }
    if (!sourceEntries.length) { setMessage('No files to transfer.'); return; }

    setTransferring(true);
    setTransferOverlaySide(fromSide);
    addLog(`Transferring ${sourceEntries.length} item(s)...`);

    const paths = sourceEntries.map((e) => e.path);

    let okCount = 0;
    let errCount = 0;
    try {
      await api.transferCopy({
        from: fromSide, to: toSide, paths, targetDir: baseTargetDir,
        onProgress: (r) => {
          const name = r.path.split('/').pop() || r.path;
          if (r.isDir) {
            const t = startTransfer(`Created: ${name}`, r.dest, 'action');
            t.succeed();
            addLog(`Created: ${r.dest}`);
          } else if (r.status === 'ok') {
            okCount++;
            const sizeLabel = r.size != null ? ` (${(r.size / 1024).toFixed(1)} KB)` : '';
            const t = startTransfer(`Copied: ${name}`, r.path, 'action');
            t.succeed();
            addLog(`Copied: ${r.path}${sizeLabel}`);
          } else {
            errCount++;
            const t = startTransfer(`Failed: ${name}`, r.path, 'action');
            t.fail(r.error || 'failed');
            addLog(`Failed: ${r.path} — ${r.error}`);
          }
        }
      });
      const summary = errCount > 0
        ? `${okCount} file(s) copied, ${errCount} failed.`
        : `${okCount} file(s) copied successfully.`;
      addLog(`Transfer complete: ${summary}`);
      await Promise.all([refreshTransferSide('left'), refreshTransferSide('right')]);
      setTransferStatusMap({});
      setModal({
        title: 'Transfer Complete',
        description: summary,
        requireInput: false,
        confirmLabel: 'OK',
        onConfirm: () => setModal(null),
        onCancel: () => setModal(null),
      });
    } catch (err) {
      addLog(`Transfer error: ${err?.message || 'failed'}`);
      await Promise.all([refreshTransferSide('left'), refreshTransferSide('right')]);
      setTransferStatusMap({});
      setModal({
        title: 'Transfer Failed',
        description: err?.message || 'An error occurred during the transfer.',
        requireInput: false,
        confirmLabel: 'OK',
        onConfirm: () => setModal(null),
        onCancel: () => setModal(null),
      });
    } finally {
      setTransferring(false);
      setTransferOverlaySide(null);
    }
  };

  const transferAllFromOther = async () => {
    const entries =
      transferRight.mode === 'local' ?
        transferRightEntries.filter((e) => e.path) :
        await fetchAllTransferEntries('right', transferRightPath || '/');
    await runTransferBatch('right', 'left', entries, transferLeftPath || '/');
  };

  const moveBetween = async (direction) => {
    if (!api) { setMessage('API bridge unavailable.'); return; }
    const toLeft = direction === 'left';
    const fromSide = toLeft ? 'right' : 'left';
    const toSide = toLeft ? 'left' : 'right';
    const selected = toLeft ? transferRightSelected : transferLeftSelected;
    if (!selected || selected.size === 0) return;

    const selectedPaths = Array.from(selected);
    const sourceEntries = (toLeft ? transferRightEntries : transferLeftEntries)
      .filter((e) => selectedPaths.includes(e.path));
    const targetDir = toLeft ? transferLeftPath || '/' : transferRightPath || '/';

    await runTransferBatch(fromSide, toSide, sourceEntries, targetDir);

    if (toLeft) {
      setTransferRightSelected(new Set());
    } else {
      setTransferLeftSelected(new Set());
    }
  };

  useEffect(() => {
    if (!connected) {
      transferLeftBindingKeyRef.current = null;
      setTransferConnected((prev) => prev.left ? { ...prev, left: false } : prev);
      setTransferLeftSelected(new Set());
      return;
    }
    if (viewMode !== 'transfer') return;
    if (transferConnecting.left) return;

    const bindingKey = [
    activeTabId,
    connection?.protocol || 'sftp',
    connection?.host || '',
    connection?.port || '',
    connection?.username || ''].
    join('|');

    if (transferConnected.left && transferLeftBindingKeyRef.current === bindingKey) {
      return;
    }

    const cfg = {
      host: connection.host,
      port: connection.port,
      username: connection.username,
      password: connection.password,
      protocol: connection.protocol || 'sftp'
    };

    setTransferLeftSelected(new Set());
    handleTransferConnect('left', cfg, remotePath || '/').then((ok) => {
      if (ok) {
        transferLeftBindingKeyRef.current = bindingKey;
        setTransferLeft(cfg);
      }
    });
  }, [
    activeTabId,
    connected,
    connection?.host,
    connection?.port,
    connection?.username,
    connection?.password,
    connection?.protocol,
    remotePath,
    transferConnected.left,
    transferConnecting.left,
    viewMode]);



  const createLocalFile = () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'New local file',
      description: 'Create a file in the current local path.',
      value: '',
      requireInput: true,
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        try {
          await handleCreateLocalFile(name);
          setModal(null);
        } catch (err) {
          setModal((m) => ({ ...m, error: err?.message || 'Failed to create file.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const createLocalFolder = () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'New local folder',
      description: 'Create a folder in the current local path.',
      value: '',
      requireInput: true,
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        try {
          await handleCreateLocalFolder(name);
          setModal(null);
        } catch (err) {
          setModal((m) => ({ ...m, error: err?.message || 'Failed to create folder.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const createRemoteFile = () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'New file',
      description: 'Create a file in the current remote path.',
      value: '',
      requireInput: true,
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        if (!name || !name.trim()) {
          setModal((m) => ({ ...m, error: 'Please enter a file name.' }));
          return;
        }
        if (name.length > 255) {
          setModal((m) => ({ ...m, error: 'File name must be 255 characters or fewer.' }));
          return;
        }
        if (remoteEntries.some((entry) => entry.name === name.trim())) {
          setModal((m) => ({ ...m, error: `A file or folder named "${name.trim()}" already exists.` }));
          return;
        }
        const target = joinRemotePath(remotePath, name);
        const t = startTransfer(`Create file: ${name}`, target, 'action');
        try {
          await api.createRemoteFile(target, '');
          t.succeed();
          setModal(null);
          refreshRemote(remotePath, { silentLog: true });
          addLog(`Created file ${target}`);
        } catch (err) {
          t.remove();
          setModal((m) => ({ ...m, error: err?.message || 'Failed to create file.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const createRemoteFolder = () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'New remote folder',
      description: 'Create a folder in the current remote path.',
      value: '',
      requireInput: true,
      confirmLabel: 'Create',
      onConfirm: async (name) => {
        if (!name || !name.trim()) {
          setModal((m) => ({ ...m, error: 'Please enter a folder name.' }));
          return;
        }
        if (name.length > 255) {
          setModal((m) => ({ ...m, error: 'Folder name must be 255 characters or fewer.' }));
          return;
        }
        const folderTarget = joinRemotePath(remotePath, name);
        const t = startTransfer(`Create folder: ${name}`, folderTarget, 'action');
        try {
          await handleCreateRemoteFolder(name);
          t.succeed();
          setModal(null);
        } catch (err) {
          t.remove();
          setModal((m) => ({ ...m, error: err?.message || 'Failed to create folder.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const getPerFileLimitBytes = () => {
    const limitMb = Number(settings?.transferLimits?.perFileSizeLimitMb) || 0;
    if (limitMb <= 0) return 0;
    return limitMb * 1024 * 1024;
  };

  const getTransferConcurrency = (mode) =>
    isWebMode && mode === 'upload' ? WEB_UPLOAD_CONCURRENCY : 3;

  const runWithConcurrency = async (items, limit, worker) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const queue = [...items];
    const workerCount = Math.max(1, Math.min(Number(limit) || 1, queue.length));
    const runners = Array.from({ length: workerCount }, async () => {
      while (queue.length > 0) {
        const next = queue.shift();
        if (!next) break;
        await worker(next);
      }
    });
    await Promise.all(runners);
  };

  const fetchRemoteListingPage = async (path, page) =>
    normalizeListingResult(await api.listRemote(path, { page }));

  const fetchAllRemoteEntries = async (path) => {
    const all = [];
    let page = 1;
    while (true) {
      const batch = await fetchRemoteListingPage(path, page);
      all.push(...batch);
      if (page >= (batch.pageCount || 1)) break;
      page += 1;
    }
    return all;
  };

  const ensureWebDownloadBase = async () => {
    if (!isWebMode) {
      return localPath || (await api.getHomeDir());
    }
    if (!canUseLocalFs) {
      throw new Error(localFsUnavailableMessage || 'Browser local filesystem is not available.');
    }
    const currentBase = localPath || (await api.getHomeDir());
    if (canPickLocalFolder && !hasPickedLocalFolder) {
      if (currentBase && currentBase !== 'Browser') {
        await refreshLocal(currentBase);
        return currentBase;
      }
      const picked = await api.pickLocalDirectory();
      const target = picked?.base || (await api.getHomeDir());
      await refreshLocal(target);
      return target;
    }
    return currentBase;
  };

  const expandRemoteDownloadTarget = async (entry, baseLocalPath, limitBytes = 0, relativeBase = '') => {
    const safeName = assertSafeLocalEntryName(entry.name, 'download name');
    const localTargetPath = joinLocalPath(baseLocalPath, safeName);
    const displayName = relativeBase ? `${relativeBase}/${safeName}` : safeName;

    if (entry.type !== 'dir') {
      if (limitBytes && Number.isFinite(entry.size) && entry.size > limitBytes) {
        return { directories: [], files: [], skippedByLimit: 1 };
      }
      return {
        directories: [],
        files: [{ remotePath: entry.path, localPath: localTargetPath, displayName }],
        skippedByLimit: 0
      };
    }

    const directories = [localTargetPath];
    const files = [];
    let skippedByLimit = 0;
    const children = await fetchAllRemoteEntries(entry.path);
    for (const child of children) {
      const expanded = await expandRemoteDownloadTarget(child, localTargetPath, limitBytes, displayName);
      directories.push(...expanded.directories);
      files.push(...expanded.files);
      skippedByLimit += expanded.skippedByLimit;
    }

    return { directories, files, skippedByLimit };
  };

  const getDownloadTargets = (item) => {
    if (item?.type === 'file' || item?.type === 'dir') return [item];
    if (remoteSelectedSet.size > 0) {
      const byPath = new Map(remoteEntries.map((entry) => [entry.path, entry]));
      const selected = Array.from(remoteSelectedSet).
      map((entryPath) => byPath.get(entryPath)).
      filter(Boolean);
      if (selected.length > 0) return selected;
    }
    if (selectedRemote) return [selectedRemote];
    return [];
  };

  const handleDownload = async (item) => {
    const targets = getDownloadTargets(item);
    if (targets.length === 0) {
      setMessage('Pick one or more files on the server side to download.');
      return;
    }
    if (!api) { setMessage('API bridge unavailable.'); return; }
    if (!connected) { setMessage('Connect to a server first.'); return; }

    const hasDir = targets.some((t) => t.type === 'dir');
    if (hasDir && isWebMode && !canUseLocalFs) {
      setMessage(localFsUnavailableMessage || `Folder downloads require browser local folder access. ${localFolderHint}`);
      return;
    }
    const label = targets.length === 1 ? targets[0].name : `${targets.length} items`;
    const labelDisplay =
      targets.length === 1 ?
        <span
          className="inline-block max-w-[18rem] align-bottom truncate"
          title={label}>
          {label}
        </span> :
        label;
    const description =
      hasDir && isWebMode ?
        <>Download "{labelDisplay}" directly into your chosen local folder. This fetches each file individually and does not create a zip on the server.</> :
      hasDir ?
        <>Downloading "{labelDisplay}" will zip folder(s) on the server before sending. Large folders may take a while to prepare.</> :
        <>Download {labelDisplay} from the server?</>;

    const doDownload = async () => {
      const limitBytes = getPerFileLimitBytes();
      if (hasDir && isWebMode) {
        let base = '';
        try {
          base = await ensureWebDownloadBase();
        } catch (err) {
          if (err?.name === 'AbortError') return;
          setMessage(err?.message || `Choose a local folder before downloading folders. ${localFolderHint}`);
          return;
        }

        try {
          const directories = new Set();
          const fileTasks = [];
          let skippedByLimit = 0;
          for (const targetItem of targets) {
            const expanded = await expandRemoteDownloadTarget(targetItem, base, limitBytes);
            expanded.directories.forEach((dirPath) => directories.add(dirPath));
            fileTasks.push(...expanded.files);
            skippedByLimit += expanded.skippedByLimit;
          }

          for (const dirPath of [...directories].sort((a, b) => a.length - b.length)) {
            await api.createLocalDir(dirPath);
          }

          const concurrency = getTransferConcurrency('download');
          let failed = 0;
          let firstError = '';
          await runWithConcurrency(fileTasks, concurrency, async (fileTask) => {
            let tracker = null;
            try {
              tracker = startTransfer(fileTask.displayName, fileTask.remotePath, 'download');
              await api.download(fileTask.remotePath, fileTask.localPath, {
                onProgress: (pct) => tracker.update(pct),
                signal: tracker.signal,
              });
              tracker.succeed();
              addLog(`Downloaded ${fileTask.remotePath}`);
            } catch (err) {
              failed += 1;
              if (!firstError) firstError = err?.message || 'Download failed.';
              if (tracker) tracker.fail(err?.message || 'Download failed.');
            }
          });

          await refreshLocal(base);
          if (fileTasks.length === 0 && directories.size > 0 && skippedByLimit === 0) {
            setMessage(`Created ${directories.size} folder(s) locally.`);
          }
          if (failed > 0 || skippedByLimit > 0) {
            const completed = fileTasks.length - failed;
            const details = [`Downloaded ${completed} of ${fileTasks.length} file(s).`];
            if (directories.size > 0) {
              details.push(`Created ${directories.size} folder(s).`);
            }
            if (skippedByLimit > 0) {
              const limitMb = Number(settings?.transferLimits?.perFileSizeLimitMb) || 0;
              details.push(`Skipped ${skippedByLimit} over ${limitMb} MB.`);
            }
            if (failed > 0 && firstError) details.push(`Error: ${firstError}`);
            setMessage(details.join(' '));
          }
        } catch (err) {
          setMessage(err?.message || 'Folder download failed.');
        }
        return;
      }

      const eligible = [];
      let skippedByLimit = 0;
      for (const targetItem of targets) {
        if (limitBytes && Number.isFinite(targetItem.size) && targetItem.size > limitBytes && targetItem.type !== 'dir') {
          skippedByLimit += 1;
        } else {
          eligible.push(targetItem);
        }
      }
      if (eligible.length === 0) {
        const limitMb = Number(settings?.transferLimits?.perFileSizeLimitMb) || 0;
        setMessage(`Skipped ${skippedByLimit} file(s) over ${limitMb} MB.`);
        return;
      }
      const base = localPath || (await api.getHomeDir());
      const concurrency = getTransferConcurrency('download');
      let failed = 0;
      let firstError = '';
      await runWithConcurrency(eligible, concurrency, async (targetItem) => {
        let tracker = null;
        try {
          const safeName = assertSafeLocalEntryName(targetItem.name, 'download name');
          const trackerName = targetItem.type === 'dir'
            ? `${safeName}.zip`
            : safeName;
          const targetPath = joinLocalPath(base, safeName);
          tracker = startTransfer(trackerName, targetItem.path, 'download');
          await api.download(targetItem.path, targetPath, {
            onProgress: (pct) => tracker.update(pct),
            signal: tracker.signal,
          });
          tracker.succeed();
          addLog(`Downloaded ${targetItem.path}`);
        } catch (err) {
          failed += 1;
          if (!firstError) firstError = err?.message || 'Download failed.';
          if (tracker) tracker.fail(err?.message || 'Download failed.');
        }
      });
      await refreshLocal(base);
      if (failed > 0 || skippedByLimit > 0) {
        const completed = eligible.length - failed;
        const details = [`Downloaded ${completed} of ${targets.length} item(s).`];
        if (skippedByLimit > 0) {
          const limitMb = Number(settings?.transferLimits?.perFileSizeLimitMb) || 0;
          details.push(`Skipped ${skippedByLimit} over ${limitMb} MB.`);
        }
        if (failed > 0 && firstError) details.push(`Error: ${firstError}`);
        setMessage(details.join(' '));
      }
    };

    setModal({
      title: 'Download',
      description,
      confirmLabel: 'Download',
      requireInput: false,
      onConfirm: () => { setModal(null); doDownload(); },
      onCancel: () => setModal(null),
    });
  };

  const sanitizeUploadRelativePath = (value) =>
  String(value || '').
  replace(/\\/g, '/').
  split('/').
  filter((part) => part && part !== '.' && part !== '..').
  join('/');

  const getUploadRelativePath = (file) => {
    const candidate =
    file?.relativePath ||
    file?.webkitRelativePath ||
    file?.name ||
    'upload.bin';
    return sanitizeUploadRelativePath(candidate) || String(file?.name || 'upload.bin');
  };

  const setUploadRelativePath = (file, relativePath) => {
    const sanitized = sanitizeUploadRelativePath(relativePath) || String(file?.name || 'upload.bin');
    try {
      Object.defineProperty(file, 'relativePath', {
        value: sanitized,
        configurable: true
      });
    } catch {
      try {
        file.relativePath = sanitized;
      } catch {
      }
    }
    return file;
  };

  const splitUploadName = (name) => {
    const safeName = assertSafeLocalEntryName(name, 'upload name');
    const dotIndex = safeName.lastIndexOf('.');
    if (dotIndex <= 0) {
      return { stem: safeName, ext: '' };
    }
    return {
      stem: safeName.slice(0, dotIndex),
      ext: safeName.slice(dotIndex)
    };
  };

  const createUniqueUploadName = (name, usedNames) => {
    const { stem, ext } = splitUploadName(name);
    let attempt = `${stem} (copy)${ext}`;
    let index = 2;
    while (usedNames.has(attempt)) {
      attempt = `${stem} (copy ${index})${ext}`;
      index += 1;
    }
    return attempt;
  };

  const formatConflictNames = (items) => {
    const names = items.slice(0, 3).map((item) => `"${truncatePath(item.name, 18)}"`);
    if (items.length > 3) {
      names.push(`and ${items.length - 3} more`);
    }
    return names.join(', ');
  };

  const promptUploadConflictRule = ({ baseRemote, fileConflicts, folderConflicts, mismatchConflicts }) =>
  new Promise((resolve) => {
    const close = (value) => {
      setModal(null);
      resolve(value);
    };

    setModal({
      title: 'Upload conflicts',
      requireInput: false,
      description:
      <div className="space-y-2">
          <p className="m-0">
            Matching names already exist in <span className="font-semibold text-white">{baseRemote}</span>.
          </p>
          {fileConflicts.length > 0 ?
        <p className="m-0">
              Files: {formatConflictNames(fileConflicts)}
            </p> :
        null}
          {folderConflicts.length > 0 ?
        <p className="m-0">
              Folders: {formatConflictNames(folderConflicts)}
            </p> :
        null}
          {mismatchConflicts.length > 0 ?
        <p className="m-0">
              File/folder mismatches cannot be safely overwritten: {formatConflictNames(mismatchConflicts)}
            </p> :
        null}
          <p className="m-0">
            Replace overwrites existing files and merges existing folders. Rename new keeps both copies.
          </p>
        </div>,
      actions: [
      { label: 'Cancel', onClick: () => close(null) },
      { label: 'Skip existing', onClick: () => close('skip') },
      { label: 'Rename new', onClick: () => close('rename') },
      { label: 'Replace / Merge', variant: 'primary', onClick: () => close('replace') }],
      onCancel: () => close(null)
    });
  });

  const planUploadBatch = async (files, baseRemote) => {
    const topLevelItems = [];
    const byTopLevel = new Map();
    for (const file of files) {
      const relativePath = getUploadRelativePath(file);
      const parts = relativePath.split('/').filter(Boolean);
      if (parts.length === 0) continue;
      const topName = assertSafeLocalEntryName(parts[0], 'upload name');
      const kind = parts.length > 1 ? 'dir' : 'file';
      let bucket = byTopLevel.get(topName);
      if (!bucket) {
        bucket = { name: topName, kind, files: [] };
        byTopLevel.set(topName, bucket);
        topLevelItems.push(bucket);
      }
      if (kind === 'dir') {
        bucket.kind = 'dir';
      }
      bucket.files.push({ file, relativePath });
    }

    const existingEntries = await fetchAllRemoteEntries(baseRemote);
    const existingByName = new Map(existingEntries.map((entry) => [entry.name, entry]));
    const conflicts = topLevelItems.
    map((item) => {
      const existing = existingByName.get(item.name);
      if (!existing) return null;
      return {
        ...item,
        existing,
        sameType: existing.type === item.kind
      };
    }).
    filter(Boolean);

    if (conflicts.length === 0) {
      return {
        files,
        skippedByConflict: 0,
        renamedByConflict: 0,
        mismatchedSkipped: 0,
        appliedRule: null
      };
    }

    let overwriteRule = settings?.transferMode?.overwriteRule || 'prompt';
    if (overwriteRule === 'prompt') {
      overwriteRule = await promptUploadConflictRule({
        baseRemote,
        fileConflicts: conflicts.filter((item) => item.kind === 'file' && item.sameType),
        folderConflicts: conflicts.filter((item) => item.kind === 'dir' && item.sameType),
        mismatchConflicts: conflicts.filter((item) => !item.sameType)
      });
    }

    if (!overwriteRule) {
      return null;
    }

    const usedNames = new Set(existingEntries.map((entry) => entry.name));
    const conflictByName = new Map(conflicts.map((item) => [item.name, item]));
    const plannedFiles = [];
    let skippedByConflict = 0;
    let renamedByConflict = 0;
    let mismatchedSkipped = 0;

    for (const item of topLevelItems) {
      const conflict = conflictByName.get(item.name);
      let finalTopLevelName = item.name;

      if (conflict) {
        if (overwriteRule === 'skip') {
          skippedByConflict += item.files.length;
          continue;
        }
        if (overwriteRule === 'replace' && !conflict.sameType) {
          skippedByConflict += item.files.length;
          mismatchedSkipped += item.files.length;
          continue;
        }
        if (overwriteRule === 'rename') {
          finalTopLevelName = createUniqueUploadName(item.name, usedNames);
          renamedByConflict += 1;
        }
      }

      usedNames.add(finalTopLevelName);
      for (const entry of item.files) {
        if (finalTopLevelName === item.name) {
          plannedFiles.push(entry.file);
          continue;
        }
        const parts = entry.relativePath.split('/').filter(Boolean);
        parts[0] = finalTopLevelName;
        plannedFiles.push(setUploadRelativePath(entry.file, parts.join('/')));
      }
    }

    return {
      files: plannedFiles,
      skippedByConflict,
      renamedByConflict,
      mismatchedSkipped,
      appliedRule: overwriteRule
    };
  };

  const readEntryBatch = async (reader) =>
  new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });

  const collectFilesFromEntry = async (entry, basePath = '') => {
    if (!entry) return [];
    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file(
          (file) => {
            const relative = sanitizeUploadRelativePath(
              basePath ? `${basePath}/${file.name}` : file.name
            );
            try {
              Object.defineProperty(file, 'relativePath', {
                value: relative,
                configurable: true
              });
            } catch {
              try {
                file.relativePath = relative;
              } catch {
              }
            }
            resolve([file]);
          },
          () => resolve([])
        );
      });
    }

    if (!entry.isDirectory) return [];
    const nextBase = sanitizeUploadRelativePath(
      basePath ? `${basePath}/${entry.name}` : entry.name
    );
    const reader = entry.createReader();
    const files = [];
    while (true) {
      const batch = await readEntryBatch(reader);
      if (!batch.length) break;
      for (const child of batch) {
        const childFiles = await collectFilesFromEntry(child, nextBase);
        files.push(...childFiles);
      }
    }
    return files;
  };

  const collectDroppedFiles = async (files, dataTransfer) => {
    const initial = Array.from(files || []).filter(Boolean);
    const items = Array.from(dataTransfer?.items || []);
    if (items.length === 0) return initial;
    const entries = items.
    map((item) =>
    typeof item?.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null
    ).
    filter(Boolean);
    if (entries.length === 0) return initial;
    const hasDirectoryEntry = entries.some((entry) => entry?.isDirectory);
    if (!hasDirectoryEntry && initial.length > 0) {
      return initial;
    }

    const nested = await Promise.all(entries.map((entry) => collectFilesFromEntry(entry)));
    const flattened = nested.flat();
    return flattened.length > 0 ? flattened : initial;
  };

  const uploadDroppedFiles = async (files, dataTransfer = null) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    if (!connected) {
      setMessage('Connect to a server first.');
      return;
    }
    const list = await collectDroppedFiles(files, dataTransfer);
    if (list.length === 0) return;

    const limitBytes = getPerFileLimitBytes();
    let skipped = 0;
    const filtered = list.filter((file) => {
      if (!limitBytes) return true;
      if (!Number.isFinite(file.size)) return true;
      if (file.size > limitBytes) {
        skipped += 1;
        return false;
      }
      return true;
    });

    const missingPath = filtered.filter((file) => !file.path);
    if (missingPath.length > 0 && !api.uploadBrowserFile) {
      setMessage('Dropped items are missing file paths.');
      return;
    }

    const baseRemote = remotePath || '/';
    const plannedUpload = await planUploadBatch(filtered, baseRemote);
    if (!plannedUpload) {
      return;
    }
    const uploadFiles = plannedUpload.files;
    if (uploadFiles.length === 0) {
      const details = [];
      if (plannedUpload.skippedByConflict > 0) {
        details.push(`Skipped ${plannedUpload.skippedByConflict} file(s) because matching names already exist.`);
      }
      if (plannedUpload.mismatchedSkipped > 0) {
        details.push('File/folder name mismatches must be renamed manually.');
      }
      setMessage(details.join(' ') || 'Nothing to upload.');
      return;
    }

    const concurrency = getTransferConcurrency('upload');
    let failed = 0;
    let firstError = '';

    const totalBytes = uploadFiles.reduce((sum, f) => sum + (Number.isFinite(f.size) ? f.size : 0), 0);
    addLog(`Starting upload: ${uploadFiles.length} file(s), ${formatSize(totalBytes)} total`);

    const uploadStartTime = Date.now();
    let bytesCompleted = 0;

    await runWithConcurrency(uploadFiles, concurrency, async (file) => {
      const uploadName = getUploadRelativePath(file);
      const fileSize = Number.isFinite(file.size) ? file.size : 0;
      const fileSizeLabel = fileSize > 1048576 ? `${(fileSize / 1048576).toFixed(1)} MB` :
        fileSize > 1024 ? `${(fileSize / 1024).toFixed(0)} KB` : `${fileSize} B`;
      const tracker = startTransfer(`${file.name} (${fileSizeLabel})`, joinRemotePath(baseRemote, uploadName), 'upload');
      const uploadOptions = {
        onProgress: (pct) => tracker.update(pct),
        signal: tracker.signal
      };
      try {
        if (file.path) {
          await api.upload(file.path, joinRemotePath(baseRemote, uploadName), uploadOptions);
        } else {
          await api.uploadBrowserFile(file, joinRemotePath(baseRemote, uploadName), uploadOptions);
        }
        bytesCompleted += fileSize;
        tracker.succeed();
        addLog(`Uploaded ${uploadName} -> ${baseRemote}`);
      } catch (err) {
        bytesCompleted += fileSize;
        if (err?.message === 'Upload cancelled') {
          tracker.fail('Cancelled');
        } else {
          failed += 1;
          if (!firstError) firstError = err?.message || 'Upload failed.';
          tracker.fail(err?.message || 'Upload failed.');
        }
      }
    });

    const elapsed = ((Date.now() - uploadStartTime) / 1000).toFixed(1);
    addLog(`Upload batch complete in ${elapsed}s`);

    await refreshRemote(baseRemote, { silentLog: true });
    if (failed > 0 || skipped > 0 || plannedUpload.skippedByConflict > 0 || plannedUpload.renamedByConflict > 0) {
      const completed = uploadFiles.length - failed;
      const details = [];
      details.push(`Uploaded ${completed} of ${list.length} file(s).`);
      if (skipped > 0) {
        const limitMb = Number(settings?.transferLimits?.perFileSizeLimitMb) || 0;
        details.push(`Skipped ${skipped} over ${limitMb} MB.`);
      }
      if (plannedUpload.skippedByConflict > 0) {
        details.push(`Skipped ${plannedUpload.skippedByConflict} existing conflict(s).`);
      }
      if (plannedUpload.renamedByConflict > 0) {
        details.push(`Renamed ${plannedUpload.renamedByConflict} conflicting top-level item(s).`);
      }
      if (plannedUpload.mismatchedSkipped > 0) {
        details.push('Some file/folder mismatches were skipped for safety.');
      }
      if (failed > 0 && firstError) {
        details.push(`Error: ${firstError}`);
      }
      setMessage(details.join(' '));
    }
  };

  const confirmAndUploadFiles = (files, dataTransfer = null) => {
    if (settings.uiux.dragDropBehavior === 'confirm') {
      const target = remotePath || '/';
      windowDragCounter.current = 0;
      setWindowDragOver(false);
      setModal({
        title: 'Upload files',
        description: `Upload selected files to ${target}?`,
        requireInput: false,
        confirmLabel: 'Upload',
        onConfirm: async () => {
          setModal(null);
          await uploadDroppedFiles(files, dataTransfer);
        },
        onCancel: () => setModal(null)
      });
      return;
    }
    uploadDroppedFiles(files, dataTransfer).catch((err) =>
    setMessage(err?.message || 'Upload failed.')
    );
  };

  const handleRemoteDropFiles = (files, dataTransfer = null) => {
    confirmAndUploadFiles(files, dataTransfer);
  };

  const handleUploadInputChange = (event) => {
    const files = Array.from(event?.target?.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    confirmAndUploadFiles(files);
  };

  const openUploadFilesPicker = () => {
    if (!connected) {
      setMessage('Connect to a server first.');
      return;
    }
    uploadFilesInputRef.current?.click();
  };

  const openUploadFolderPicker = () => {
    if (!connected) {
      setMessage('Connect to a server first.');
      return;
    }
    uploadFolderInputRef.current?.click();
  };

  const renameRemote = (item) => {
    const targetItem = item || selectedRemote;
    if (!targetItem) return;
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'Rename / Move',
      description: 'Enter a new name or full path.',
      value: targetItem.name,
      requireInput: true,
      confirmLabel: 'Apply',
      onConfirm: async (name) => {
        try {
          const target = joinRemotePath(remoteParent(targetItem.path), name);
          await api.renameRemote(targetItem.path, target);
          setModal(null);
          refreshRemote(remotePath, { silentLog: true });
          addLog(`Renamed ${targetItem.path} to ${target}`);
        } catch (err) {
          setModal((m) => ({ ...m, error: err?.message || 'Rename failed.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const deleteRemote = async (itemOrItems) => {
    const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems || selectedRemote].filter(Boolean);
    if (items.length === 0) return;
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    const doDelete = async () => {
      for (const targetItem of items) {
        const tracker = startTransfer(`Delete ${targetItem.name}`, targetItem.path, 'action');
        setDeletingPaths((prev) => new Set(prev).add(targetItem.path));
        try {
          await api.deleteRemote(targetItem.path);
          tracker.succeed();
          addLog(`Deleted remote ${targetItem.path}`);
          setRemoteEntries((prev) => prev.filter((e) => e.path !== targetItem.path));
          setRemoteSelectedSet((prev) => {
            const next = new Set(prev);
            next.delete(targetItem.path);
            return next;
          });
        } catch (err) {
          tracker.fail(err.message);
          setMessage(err.message);
        } finally {
          setDeletingPaths((prev) => {
            const next = new Set(prev);
            next.delete(targetItem.path);
            return next;
          });
        }
      }
      refreshRemote(remotePath, { silentLog: true });
    };
    if (!settings.uiux.confirmDelete) {
      doDelete();
      return;
    }
    const label = items.length === 1 ? truncatePath(items[0].name, 30) : `${items.length} items`;
    setModal({
      title: 'Delete',
      description: `Delete ${label}?`,
      value: '',
      requireInput: false,
      confirmLabel: 'Delete',
      onConfirm: () => {
        setModal(null);
        doDelete();
      },
      onCancel: () => setModal(null)
    });
  };

  const archiveRemote = (itemOrItems) => {
    const items = Array.isArray(itemOrItems) ? itemOrItems : [itemOrItems || selectedRemote].filter(Boolean);
    if (items.length === 0) return;
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    if (!canUseArchive) {
      setMessage(archiveUnavailableMessage);
      return;
    }
    const defaultName = items.length === 1 ? `${items[0].path}.zip` : `${remotePath}/archive.zip`;
    setModal({
      title: 'Archive',
      description: items.length === 1 ? 'Save as zip at remote path' : `Archive ${items.length} items as zip`,
      value: defaultName,
      requireInput: true,
      confirmLabel: 'Archive',
      onConfirm: async (dest) => {
        const paths = items.map((i) => i.path);
        const tracker = startTransfer('Archive', items.length === 1 ? items[0].name : `${items.length} items`);
        setModal(null);
        setZipInProgress(true);
        try {
          await api.archiveRemote(paths, dest || undefined);
          await refreshRemote(remotePath, { silentLog: true });
          tracker.succeed();
          addLog(`Archived ${paths.join(', ')} -> ${dest || ''}`);
        } catch (err) {
          tracker.fail(err.message);
          setMessage(err.message);
        } finally {
          setZipInProgress(false);
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const unarchiveRemote = (item) => {
    const targetItem = item || selectedRemote;
    if (!targetItem || targetItem.type !== 'file') return;
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    if (!canUseArchive) {
      setMessage(archiveUnavailableMessage);
      return;
    }
    setModal({
      title: 'Unarchive',
      description: 'Extract zip into remote path',
      value: remotePath || remoteParent(targetItem.path),
      requireInput: true,
      confirmLabel: 'Extract',
      onConfirm: async (targetDir) => {
        const tracker = startTransfer('Unarchive', targetItem.name);
        setModal(null);
        setZipInProgress(true);
        try {
          await api.unarchiveRemote(targetItem.path, targetDir || undefined);
          await refreshRemote(remotePath, { silentLog: true });
          tracker.succeed();
          addLog(`Unarchived ${targetItem.path} into ${targetDir}`);
        } catch (err) {
          tracker.fail(err.message);
          setMessage(err.message);
        } finally {
          setZipInProgress(false);
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const calculateFolderSize = async (folderPath, folderName) => {
    if (!api) { setMessage('API bridge unavailable.'); return; }
    setFolderSizeLoading((prev) => { const next = new Set(prev); next.add(folderPath); return next; });
    const tracker = startTransfer(`Folder size: ${folderName}`, folderPath, 'action');
    addLog(`[Folder Size] Starting calculation for ${folderPath}`);
    if (typeof api.folderSize === 'function') {
      try {
        const totalSize = await api.folderSize(folderPath);
        setFolderSizeMap((prev) => ({ ...prev, [folderPath]: totalSize }));
        updateTransfer(tracker.id, { name: `Folder size: ${folderName} — ${formatSize(totalSize)} (${totalSize.toLocaleString()} bytes)` });
        tracker.succeed();
        addLog(`[Folder Size] Complete — ${folderName}: ${formatSize(totalSize)} (${totalSize.toLocaleString()} bytes)`);
        setFolderSizeLoading((prev) => {
          const next = new Set(prev);
          next.delete(folderPath);
          return next;
        });
        return;
      } catch (err) {
        addLog(`[Folder Size] Direct size failed for ${folderPath}, falling back to scan: ${err.message || 'Unknown error'}`);
      }
    }
    let totalSize = 0;
    let fileCount = 0;
    let dirCount = 0;
    let dirsScanned = 0;
    const walk = async (dir) => {
      const items = [];
      let page = 1;
      try {
        while (true) {
          const batch = normalizeListingResult(await api.listRemote(dir, { page }));
          items.push(...batch);
          if (page >= (batch.pageCount || 1)) break;
          page += 1;
        }
      } catch (err) {
        addLog(`[Folder Size] Could not read ${dir}: ${err.message || 'error'}`);
        return;
      }
      const files = items.filter((i) => i.type !== 'dir');
      const dirs = items.filter((i) => i.type === 'dir');
      for (const f of files) {
        totalSize += f.size || 0;
        fileCount += 1;
      }
      dirCount += dirs.length;
      dirsScanned += 1;
      addLog(`[Folder Size] Scanned ${dir} — ${files.length} files, ${dirs.length} subfolders (running total: ${formatSize(totalSize)})`);
      updateTransfer(tracker.id, { name: `Folder size: ${folderName} — ${formatSize(totalSize)} (${fileCount} files, ${dirsScanned} folders scanned)` });
      for (const d of dirs) {
        await walk(d.path);
      }
    };
    try {
      await walk(folderPath);
      setFolderSizeMap((prev) => ({ ...prev, [folderPath]: totalSize }));
      updateTransfer(tracker.id, { name: `Folder size: ${folderName} — ${formatSize(totalSize)} (${totalSize.toLocaleString()} bytes) across ${fileCount} files in ${dirCount + 1} folders` });
      tracker.succeed();
      addLog(`[Folder Size] Complete — ${folderName}: ${formatSize(totalSize)} (${totalSize.toLocaleString()} bytes) across ${fileCount} files in ${dirCount + 1} folders`);
    } catch (err) {
      tracker.fail(err.message || 'Unknown error');
      addLog(`[Folder Size] Failed for ${folderPath}: ${err.message || 'Unknown error'}`);
      setMessage(`Failed to calculate size for ${folderName}: ${err.message || 'Unknown error'}`);
    } finally {
      setFolderSizeLoading((prev) => { const next = new Set(prev); next.delete(folderPath); return next; });
    }
  };

  const moveRemote = (item) => {
    const targetItem = item || selectedRemote;
    if (!targetItem) return;
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    setModal({
      title: 'Move to',
      description: 'Enter target path',
      value: targetItem.path,
      requireInput: true,
      confirmLabel: 'Move',
      onConfirm: async (target) => {
        try {
          await api.moveRemote(targetItem.path, target);
          setModal(null);
          refreshRemote(remotePath, { silentLog: true });
          addLog(`Moved ${targetItem.path} to ${target}`);
        } catch (err) {
          setModal((m) => ({ ...m, error: err?.message || 'Move failed.' }));
        }
      },
      onCancel: () => setModal(null)
    });
  };

  const handleDragMoveEntries = useCallback(async (paths, targetDir) => {
    if (!api || !connected) return;
    const resolvedTarget = targetDir === '..' ? remoteParent(remotePath) : targetDir;
    try {
      for (const sourcePath of paths) {
        const name = sourcePath.split('/').pop();
        const dest = joinRemotePath(resolvedTarget, name);
        if (sourcePath !== dest) {
          await api.renameRemote(sourcePath, dest);
          addLog(`Moved ${sourcePath} -> ${dest}`);
        }
      }
      await refreshRemote(remotePath, { silentLog: true });
    } catch (err) {
      setMessage(err?.message || 'Move failed.');
    }
  }, [api, connected, remotePath, refreshRemote, addLog, setMessage]);

  const localPanelActions = useMemo(
    () => {
      if (!canUseLocalFs) {
        return [
        { label: 'Why unavailable?', onClick: () => setMessage(localFsUnavailableMessage) },
        { label: 'Refresh', icon: <RiRefreshLine className={loadingLocal ? 'spin-fast' : ''} />, onClick: () => refreshLocal(localPath), disabled: true }];
      }

      const actions = [];
      if (canPickLocalFolder) {
        actions.push({
          label: hasPickedLocalFolder ? 'Change folder' : 'Choose folder',
          icon: <RiFolderOpenLine />,
          onClick: async () => {
            try {
              const picked = await api.pickLocalDirectory();
              const target = picked?.base || (await api.getHomeDir());
              await refreshLocal(target);
              setMessage('');
            } catch (err) {
              if (err?.name === 'AbortError') return;
              setMessage(err?.message || `Failed to choose local folder. ${localFolderHint}`);
            }
          }
        });
      }

      actions.push({
        label: 'Refresh',
        icon: <RiRefreshLine className={loadingLocal ? 'spin-fast' : ''} />,
        onClick: () => refreshLocal(localPath),
        disabled: canPickLocalFolder && !hasPickedLocalFolder
      });
      actions.push({
        label: 'New file',
        onClick: createLocalFile,
        disabled: canPickLocalFolder && !hasPickedLocalFolder
      });
      actions.push({
        label: 'New folder',
        onClick: createLocalFolder,
        disabled: canPickLocalFolder && !hasPickedLocalFolder
      });
      return actions;
    },

    [
    api,
    canUseLocalFs,
    localFsUnavailableMessage,
    canPickLocalFolder,
    hasPickedLocalFolder,
    createLocalFile,
    createLocalFolder,
    localPath,
    loadingLocal,
    refreshLocal]
  );

  const remotePanelActions = useMemo(
    () => {
      const hasSelectedRemoteFiles =
      remoteSelectedSet.size > 0 ?
      Array.from(remoteSelectedSet).some((entryPath) =>
      remoteEntries.some((entry) => entry.path === entryPath)
      ) :
      selectedRemote != null;
      const actions = [
      { label: 'Refresh', icon: <RiRefreshLine className={loadingRemote ? 'spin-fast' : ''} />, onClick: () => refreshRemote(remotePath), disabled: !connected },
      { label: 'New file', icon: <RiFileAddLine />, onClick: createRemoteFile, disabled: !connected },
      { label: 'New folder', icon: <RiFolderAddLine />, onClick: createRemoteFolder, disabled: !connected },
      { label: 'Download selected', icon: <RiDownload2Line />, onClick: () => handleDownload(), disabled: !connected || !hasSelectedRemoteFiles }];
      if (!isWebMode || canBrowserUpload) {
        actions.push({ label: 'Upload files', icon: <RiUpload2Line />, onClick: openUploadFilesPicker, disabled: !connected });
        actions.push({ label: 'Upload folder', icon: <RiUploadCloud2Line />, onClick: openUploadFolderPicker, disabled: !connected });
      }
      return actions;
    },

    [
    isWebMode,
    canBrowserUpload,
    connected,
    remotePath,
    loadingRemote,
    refreshRemote,
    createRemoteFile,
    createRemoteFolder,
    handleDownload,
    remoteEntries,
    remoteSelectedSet,
    selectedRemote,
    openUploadFilesPicker,
    openUploadFolderPicker]
  );

  const listContainerStyle = { flex: 1, minHeight: 0 };
  const statusText = connecting ? 'Connecting...' : connected ? 'Connected' : 'Disconnected';
  const localLogins = useMemo(
    () => savedLogins.filter((entry) => !entry.encrypted).sort((a, b) => a.name.localeCompare(b.name)),
    [savedLogins]
  );
  const vaultLogins = useMemo(
    () => savedLogins.filter((entry) => entry.encrypted).sort((a, b) => a.name.localeCompare(b.name)),
    [savedLogins]
  );
  const startupPickerVaultLocked = useMemo(
    () =>
      Boolean(
        startupPicker &&
        isWebMode &&
        canUseVault &&
        vaultStatus?.hasMaster &&
        !vaultStatus?.unlocked &&
        startupPicker.some((entry) => entry.encrypted)
      ),
    [startupPicker, isWebMode, canUseVault, vaultStatus]
  );
  const startupPickerSelectedEntries = useMemo(
    () => startupPicker ? startupPicker.filter((entry) => startupPickerSelected.has(entry.name)) : [],
    [startupPicker, startupPickerSelected]
  );
  const startupPickerSelectedNeedsUnlock = useMemo(
    () => startupPickerVaultLocked && startupPickerSelectedEntries.some((entry) => entry.encrypted),
    [startupPickerVaultLocked, startupPickerSelectedEntries]
  );
  const isHiddenEntry = (entry) => entry?.name?.startsWith('.');
  const visibleLocalEntries = useMemo(
    () =>
    settings.uiux.showHiddenFiles ?
    localEntries :
    localEntries.filter((entry) => !isHiddenEntry(entry)),
    [localEntries, settings.uiux.showHiddenFiles]
  );
  const visibleRemoteEntries = useMemo(
    () =>
    settings.uiux.showHiddenFiles ?
    remoteEntries :
    filterListingResult(remoteEntries, (entry) => !isHiddenEntry(entry)),
    [remoteEntries, settings.uiux.showHiddenFiles]
  );
  const visibleTransferLeftEntries = useMemo(
    () =>
    settings.uiux.showHiddenFiles ?
    transferLeftEntries :
    filterListingResult(transferLeftEntries, (entry) => !isHiddenEntry(entry)),
    [transferLeftEntries, settings.uiux.showHiddenFiles]
  );
  const visibleTransferRightEntries = useMemo(
    () =>
    settings.uiux.showHiddenFiles ?
    transferRightEntries :
    filterListingResult(transferRightEntries, (entry) => !isHiddenEntry(entry)),
    [transferRightEntries, settings.uiux.showHiddenFiles]
  );

  const handleConnectSavedLoginFromModal = useCallback(async (entry) => {
    if (connectingLoginName) return;
    setConnectingLoginName(entry.name);
    try {
      await connectSavedLogin(entry);
    } finally {
      setConnectingLoginName(null);
    }
  }, [connectSavedLogin, connectingLoginName]);

  const normalizeConfig = (config) => {
    const protocol = config.protocol || 'sftp';
    return {
      ...config,
      protocol,
      port: config.port || (protocol === 'ftps' ? 21 : 2022)
    };
  };

  const openPendingDeepLink = async (config) => {
    const pending = pendingDeepLinkRef.current;
    if (!pending) return;
    if (!matchesDeepLinkConnection(pending, config)) return;
    pendingDeepLinkRef.current = null;
    if (!pending.path) return;
    await openDeepLinkPath(pending.path);
  };

  useEffect(() => {
    if (!connected) return;
    void openPendingDeepLink(connection);
  }, [connected, connection]);

  useEffect(() => {
    if (viewMode === 'edit') setMobilePanelTab('files');
    else if (viewMode === 'transfer') setMobilePanelTab('other');
    else if (viewMode === 'settings') setMobilePanelTab('activity');
  }, [viewMode]);

  useEffect(() => {
    const handler = () => {
      setIsMobileLayout(window.innerWidth < 1024);
      setCollapseActionBar(window.innerWidth < 1467);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const performSave = async (entry, encrypt, onSuccess) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return false;
    }
    if (encrypt) {

      const status = await api.vaultStatus();
      setVaultStatus(status);
      if (!status.unlocked) {
        if (status.hasMaster) {
          setModal({
            title: 'Unlock vault',
            description: 'Enter your master password',
            value: '',
            requireInput: true,
            confirmLabel: 'Unlock',
            onConfirm: async (pass) => {
              await api.vaultUnlock(pass);
              const refreshed = await api.vaultStatus();
              setVaultStatus(refreshed);
              setModal(null);
              const ok = await saveLogin(entry, true);
              if (ok && onSuccess) await onSuccess();
            },
            onCancel: () => setModal(null)
          });
          return false;
        }
        setModal({
          title: 'Set master password',
          description: 'Create a master password to encrypt logins.',
          value: '',
          requireInput: true,
          confirmLabel: 'Set',
          onConfirm: async (pass) => {
            await api.vaultSetMaster(pass);
            const refreshed = await api.vaultStatus();
            setVaultStatus(refreshed);
            setModal(null);
            const ok = await saveLogin(entry, true);
            if (ok && onSuccess) await onSuccess();
          },
          onCancel: () => setModal(null)
        });
        return false;
      }
    }
    const ok = await saveLogin(entry, encrypt);
    if (ok && onSuccess) await onSuccess();
    return ok;
  };

  const openNewLogin = async () => {
    if (isWebMode && connected) {
      if (disconnectBlocked) {
        setMessage(disconnectBlockedMessage);
        return;
      }
      await disconnect();
      setLoginDraft(createConnection());
      setShowPassword(false);
      setLoadingSavedLogin(true);
      try {
        const list = await api.vaultList();
        setSavedLogins(list);
      } catch { /* ignore */ } finally {
        setLoadingSavedLogin(false);
      }
      return;
    }
    setLoginDraft(createConnection());
    setShowPassword(false);
    setNewLoginOpen(true);
  };

  const openSavedLogins = async () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    try {
      const [status, list] = await Promise.all([api.vaultStatus(), api.vaultList()]);
      setVaultStatus(status);
      setSavedLogins(list);
      const localCount = list.filter((item) => !item.encrypted).length;
      setSavedLoginsTab(canUseVault ? localCount === 0 ? 'vault' : 'local' : 'local');
      setVaultPassword('');
      setVaultError('');
      setSavedLoginsOpen(true);
    } catch (err) {
      setMessage(err.message);
    }
  };

  const promptLoginForDeepLink = async (payload) => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    try {
      const [status, list] = await Promise.all([api.vaultStatus(), api.vaultList()]);
      setVaultStatus(status);
      setSavedLogins(list);
      const localCount = list.filter((item) => !item.encrypted).length;
      setSavedLoginsTab(canUseVault ? localCount === 0 ? 'vault' : 'local' : 'local');
      setVaultPassword('');
      setVaultError('');
      if (list.length === 0) {
        setSavedLoginsOpen(false);
        openNewLoginWithPayload(payload);
        return;
      }
      setNewLoginOpen(false);
      setSavedLoginsOpen(true);
    } catch (err) {
      setMessage(err.message);
    }
  };

  const handleDraftConnect = async () => {
    if (!loginDraft.password) { setMessage('Please enter a password before connecting.'); return; }
    const cfg = normalizeConfig(loginDraft);
    setNewLoginOpen(false);
    await connectWithConfig(cfg);
  };

  const getFriendlyLoginError = (msg) => {
    if (!msg) return msg;
    const l = msg.toLowerCase();
    if (l.includes('authentication') || l.includes('all configured') || l.includes('password') || l.includes('credential') || l.includes('permission denied'))
      return 'Incorrect username or password. Please check your credentials and try again.';
    if (l.includes('timeout') || l.includes('timed out'))
      return 'Connection timed out. Check the server address and try again.';
    if (l.includes('refused') || l.includes('econnrefused'))
      return 'Connection refused. Check the server address and port are correct.';
    if (l.includes('not allowed') || l.includes('origin'))
      return 'This server is not allowed. Make sure you are using a Kinetic Hosting server.';
    return msg;
  };

  const handleWebLoginConnect = async () => {
    if (!loginDraft.password) { setMessage('Please enter a password before connecting.'); return; }
    const cfg = normalizeConfig(loginDraft);
    await connectWithConfig(cfg);
    if (webTransferMode) setViewMode('transfer');
  };

  const handleWebLoginSaveAndConnect = () => {
    const cfg = normalizeConfig(loginDraft);
    setSaveModal({
      name: loginDraft.username ? `${loginDraft.username}@${loginDraft.host}` : (loginDraft.host || ''),
      description: '',
      encrypt: false,
      config: cfg
    });
  };

  const handleDraftSaveAndConnect = () => {
    const cfg = normalizeConfig(loginDraft);
    setSaveModal({
      name: loginDraft.username ? `${loginDraft.username}@${loginDraft.host}` : (loginDraft.host || ''),
      description: '',
      encrypt: canUseVault,
      config: cfg
    });
    setNewLoginOpen(false);
  };

  const handleSaveModalConfirm = async () => {
    if (!saveModal) return;
    const entry = {
      ...saveModal.config,
      name: saveModal.name,
      description: saveModal.description
    };
    await performSave(entry, saveModal.encrypt, async () => {
      setSaveModal(null);
      await connectWithConfig(saveModal.config);
    });
  };



  const handleExportLogs = async () => {
    if (!api) {
      setMessage('API bridge unavailable.');
      return;
    }
    try {
      const home = await api.getHomeDir();
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const defaultName = `kinetic-logs-${stamp}.txt`;
      const defaultPath = home ? joinLocalPath(home, defaultName) : defaultName;
      setModal({
        title: 'Export logs',
        description: 'Save the activity log to a file path.',
        value: defaultPath,
        requireInput: true,
        confirmLabel: 'Export',
        onConfirm: async (targetPath) => {
          try {
            await api.writeLocalFile(targetPath, logs.slice().reverse().join('\n'));
            addLog(`Exported logs to ${targetPath}`);
          } catch (err) {
            setMessage(err.message);
          } finally {
            setModal(null);
          }
        },
        onCancel: () => setModal(null)
      });
    } catch (err) {
      setMessage(err.message);
    }
  };
  return (
    <ErrorBoundary onError={(err) => setMessage(err?.message || 'Render error.')}>
      <div
        className="h-screen flex flex-col bg-transparent text-textPrimary overflow-hidden"
        onDragEnter={(e) => {
          if (!connected || settings.uiux.dragDropBehavior === 'disabled') return;
          if (!e.dataTransfer?.types?.includes('Files')) return;
          e.preventDefault();
          windowDragCounter.current += 1;
          setWindowDragOver(true);
        }}
        onDragOver={(e) => {
          if (!connected || settings.uiux.dragDropBehavior === 'disabled') return;
          if (!e.dataTransfer?.types?.includes('Files')) return;
          e.preventDefault();
        }}
        onDragLeave={() => {
          windowDragCounter.current -= 1;
          if (windowDragCounter.current <= 0) {
            windowDragCounter.current = 0;
            setWindowDragOver(false);
          }
        }}
        onDrop={() => {
          windowDragCounter.current = 0;
          setWindowDragOver(false);
        }}>
      {windowDragOver && !modal &&
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => {
            windowDragCounter.current = 0;
            setWindowDragOver(false);
          }}
          onDrop={(e) => {
            e.stopPropagation();
            windowDragCounter.current = 0;
            setWindowDragOver(false);
            if (!connected || settings.uiux.dragDropBehavior === 'disabled') return;
            const files = Array.from(e.dataTransfer?.files || []);
            if (files.length === 0) return;
            e.preventDefault();
            confirmAndUploadFiles(files, e.dataTransfer);
          }}>
          <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-accent/60 bg-surface/80 px-12 py-10 pointer-events-none">
            <RiUpload2Line className="text-accent text-5xl" />
            <span className="text-lg font-semibold text-white">Drop files to upload</span>
            <span className="text-sm text-textMuted">Files will be uploaded to {remotePath || '/'}</span>
          </div>
        </div>
      }
      <HeaderBar
          connected={connected}
          title={isWebMode ? 'Web SFTP Console' : 'Desktop SFTP Console'}
          onMinimize={canUseWindowControls ? () => api?.minimizeWindow() : undefined}
          onClose={canUseWindowControls ? () => api?.closeWindow() : undefined} />
      <div data-sherman="tabs">
      <TabBar
          tabs={tabs}
          activeTabId={activeTabId}
          onSwitch={switchToTab}
          onAdd={handleAddTab}
          onClose={handleCloseTab} />
      </div>


      <div className="border-b border-border bg-surface/90 px-4 py-3 shrink-0">
        <div className="flex flex-col gap-4">
          {!showWebLoginScreen && !showDesktopLoginScreen ?
          <ConnectionForm
              onNewLogin={openNewLogin}
              onSavedLogins={openSavedLogins}
              showSavedLogins={!isWebMode} /> :
          null}

          {!showWebLoginScreen && !showDesktopLoginScreen && message ? <p key={message} className="text-sm text-red-300 message-auto-fade">{message}</p> : null}
        </div>
      </div>

      <input
        ref={uploadFilesInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleUploadInputChange} />
      <input
        ref={uploadFolderInputRef}
        type="file"
        multiple
        webkitdirectory=""
        directory=""
        className="hidden"
        onChange={handleUploadInputChange} />

      <main className="flex-1 min-h-0 flex flex-col gap-4 p-4 overflow-hidden">
        {showWebLoginScreen ?
        <section className="flex-1 min-h-0 overflow-y-auto">
            {loadingSavedLogin ?
            <div className="flex min-h-full items-center justify-center py-8 px-4">
              <div className="glass-panel rounded-2xl p-8 flex flex-col items-center gap-4">
                <RiLoader4Line className="text-accent text-3xl animate-spin" />
                <p className="text-sm text-textMuted">Checking for saved logins...</p>
              </div>
            </div> : openingLoginsStatus ?
            <div className="flex min-h-full items-center justify-center py-8 px-4">
              <div className="glass-panel rounded-2xl p-8 flex flex-col items-center gap-4">
                <RiLoader4Line className="text-accent text-3xl animate-spin" />
                <p className="text-sm text-white font-medium">{openingLoginsStatus}</p>
              </div>
            </div> : startupPicker ?
            <div className="flex min-h-full items-center justify-center py-8 px-4">
              <div className="glass-panel rounded-2xl p-8 w-full max-w-md flex flex-col gap-5">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-textMuted">Web SFTP Client</p>
                  <h2 className="text-xl font-semibold text-white m-0">Choose a server</h2>
                  <p className="text-sm text-textMuted">Select one or more saved logins to open.</p>
                </div>
                {startupPickerVaultLocked ?
                  <div className="rounded-lg border border-border bg-inset/60 p-3">
                    <div className="text-sm text-textMuted">
                      Some saved logins are encrypted. Unlock with your saved-logins key to use them.
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        className="flex-1 rounded-lg border border-border bg-inset/70 px-3 py-2 text-sm text-white placeholder:text-textMuted focus:border-accent focus:outline-none"
                        placeholder="Saved logins key"
                        type="password"
                        value={vaultPassword}
                        onChange={(e) => setVaultPassword(e.target.value)} />
                      <button
                        type="button"
                        className="pill-button px-4 py-2"
                        onClick={handleVaultUnlock}
                        disabled={vaultUnlocking || !vaultPassword}>
                        {vaultUnlocking ? 'Unlocking...' : 'Unlock'}
                      </button>
                    </div>
                    {vaultError ? <p className="mt-2 text-xs text-red-300">{vaultError}</p> : null}
                  </div> :
                  null}
                <div className="flex flex-col gap-2">
                  {startupPicker.map((entry) => (
                    <label
                      key={entry.name}
                      className={`flex items-center gap-3 rounded-lg border bg-inset/60 px-4 py-3 transition-colors ${
                        startupPickerVaultLocked && entry.encrypted ?
                          'border-border/60 opacity-70 cursor-not-allowed' :
                          'border-border cursor-pointer hover:border-accent/50'
                      }`}>
                      <input
                        type="checkbox"
                        className="accent-accent w-4 h-4 shrink-0"
                        disabled={startupPickerVaultLocked && entry.encrypted}
                        checked={startupPickerSelected.has(entry.name)}
                        onChange={(e) => setStartupPickerSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(entry.name); else next.delete(entry.name);
                          return next;
                        })} />
                      <div className="flex flex-col min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <span className="text-sm font-medium text-textPrimary truncate">{entry.name}</span>
                          {entry.encrypted ? <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-textMuted">Encrypted</span> : null}
                        </div>
                        <span className="text-xs text-textMuted truncate">{entry.host}:{entry.port} · {entry.username}</span>
                      </div>
                      <button
                        type="button"
                        className="ml-auto shrink-0 ghost-button px-3 py-1 text-sm"
                        disabled={startupPickerVaultLocked && entry.encrypted}
                        onClick={() => { setStartupPicker(null); connectSavedLogin(entry); }}>
                        {startupPickerVaultLocked && entry.encrypted ? 'Unlock required' : 'Connect'}
                      </button>
                    </label>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="pill-button w-full justify-center py-2.5 text-base"
                    disabled={startupPickerSelected.size === 0 || startupPickerSelectedNeedsUnlock}
                    onClick={() => {
                      const entries = startupPicker.filter((l) => startupPickerSelected.has(l.name));
                      openLoginsInTabs(entries);
                    }}>
                    {startupPickerSelected.size > 1 ? `Open ${startupPickerSelected.size} tabs` : 'Connect'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button w-full justify-center py-2 text-sm"
                    disabled={startupPickerVaultLocked}
                    onClick={() => openLoginsInTabs(startupPicker)}>
                    Open all ({startupPicker.length})
                  </button>
                  <button
                    type="button"
                    className="ghost-button w-full justify-center py-2 text-sm text-textMuted"
                    onClick={() => setStartupPicker(null)}>
                    Sign in manually
                  </button>
                </div>
              </div>
            </div> :
            <div className="flex min-h-full items-center justify-center py-8 px-4">
              <div className="w-full max-w-6xl flex flex-col lg:flex-row gap-8 items-stretch">

                <div className="glass-panel rounded-2xl p-8 w-full lg:flex-1" data-sherman="login-form">
                  <div className="space-y-1 mb-5">
                    <p className="text-xs font-semibold text-textMuted">
                      {webTransferMode ? 'Step 1 — Your Kinetic Server' : 'Web SFTP Client'}
                    </p>
                    <h2 className="text-xl font-semibold text-white m-0">Sign in to your server</h2>
                    <p className="text-sm text-textMuted leading-relaxed">
                      Your SFTP info can be found in your game panel under the{' '}
                      <strong className="text-white">File Manager</strong> tab. Your password is the same as your game panel account.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3">
                    <input
                      className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3.5 text-base text-white placeholder:text-textMuted outline-none"
                      placeholder="Server address"
                      value={loginDraft.host}
                      autoFocus
                      onChange={(e) => {
                        const raw = e.target.value;
                        const parsed = parseHostInput(raw, loginDraft.protocol);
                        setLoginDraft((prev) => ({
                          ...prev,
                          host: parsed.host || raw,
                          protocol: parsed.host ? parsed.protocol : prev.protocol,
                          port: parsed.port ?? prev.port
                        }));
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value;
                        const parsed = parseHostInput(raw, loginDraft.protocol);
                        if (!parsed.host) return;
                        setLoginDraft((prev) => ({
                          ...prev,
                          host: parsed.host,
                          protocol: parsed.protocol,
                          port: parsed.port ?? prev.port
                        }));
                      }} />
                    <input
                      className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3.5 text-base text-white placeholder:text-textMuted outline-none"
                      placeholder="Username"
                      value={loginDraft.username}
                      onChange={(e) => setLoginDraft((prev) => ({ ...prev, username: e.target.value }))} />
                    <div className="relative">
                      <input
                        className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3.5 pr-10 text-base text-white placeholder:text-textMuted outline-none"
                        placeholder="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginDraft.password}
                        onChange={(e) => setLoginDraft((prev) => ({ ...prev, password: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          if (connecting || !loginDraft.host || !loginDraft.username || !loginDraft.password || !isKineticHost(loginDraft.host)) return;
                          handleWebLoginConnect();
                        }} />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-textMuted hover:text-white window-no-drag"
                        tabIndex={-1}>
                        {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                      </button>
                    </div>
                  </div>
                  {message && !connecting ?
                  <div key={message} className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    {getFriendlyLoginError(message)}
                  </div> : null}
                  {isWebMode && loginDraft.host && !isKineticHost(loginDraft.host) ?
                  <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                      This web client is only available for Kinetic Hosting servers. To connect to other servers, download the{' '}
                      <a
                        href="https://kinetichosting.com/kinetic-sftp"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-red-100">
                        desktop app
                      </a>{' '}
                      instead.
                    </div> :
                  null}
                  <div className="mt-5 flex flex-col gap-2">
                    <button
                      type="button"
                      className="pill-button w-full py-3"
                      onClick={handleWebLoginConnect}
                      disabled={
                        connecting ||
                        !loginDraft.host ||
                        !loginDraft.username ||
                        (isWebMode && !isKineticHost(loginDraft.host))
                      }>
                      {connecting ? <span className="flex items-center justify-center gap-2"><RiLoader4Line className="animate-spin" /> Connecting...</span> : webTransferMode ? 'Connect & Go to Transfer' : 'Connect'}
                    </button>
                    <button
                      type="button"
                      className="ghost-button w-full py-2"
                      onClick={handleWebLoginSaveAndConnect}
                      disabled={
                        connecting ||
                        !loginDraft.host ||
                        !loginDraft.username ||
                        (isWebMode && !isKineticHost(loginDraft.host))
                      }>
                      Save & Connect
                    </button>
                    {savedLogins.length > 0 &&
                      <button
                        type="button"
                        className="ghost-button w-full py-2 text-textMuted"
                        onClick={() => setSavedLoginsOpen(true)}>
                        Saved logins ({savedLogins.length})
                      </button>
                    }
                  </div>
                </div>

                {(() => {
                  const slides = webTransferMode ? [
                    {
                      label: 'Step 1 of 4',
                      title: 'Login with your Kinetic server',
                      body: 'Enter the SFTP info for your new Kinetic server on the left. Some info may be pre-filled for you. Your password is the same as your game panel account.'
                    },
                    {
                      label: 'Step 2 of 4',
                      title: 'Login to your old host',
                      body: "Once connected you'll be taken to the Transfer tab. Enter the SFTP credentials for your old provider there."
                    },
                    {
                      label: 'Step 3 of 4',
                      title: 'Press Transfer',
                      body: 'Hit Transfer and your files will start moving from your old server to your new Kinetic server. Make sure to keep this tab open while the transfer is running.'
                    },
                    {
                      label: 'Step 4 of 4',
                      title: "You're done!",
                      body: 'All your files will have been transferred to your new Kinetic server, ready to go.'
                    }
                  ] : [
                    {
                      label: 'Welcome',
                      title: 'Welcome to Kinetic Web SFTP',
                      body: 'Get all the perks of a full SFTP client without downloading a thing. Manage, edit, and transfer your game server files right from your browser.'
                    },
                    {
                      label: 'What is SFTP?',
                      title: 'Built for large files & big transfers',
                      body: null,
                      list: [
                        'No file size limits — upload files of any size',
                        'Direct server connection, much faster than the browser file manager',
                        'Bulk transfer entire folder structures at once',
                        'Reliable — large transfers won\'t time out or fail mid-way',
                        'Edit files with a built-in code editor with syntax highlighting'
                      ]
                    },
                    {
                      label: 'Moving Host?',
                      title: 'Moving Server, or From Another Host?',
                      body: "Using our SFTP Transfer tool you can easily move from one Kinetic server to another, or bring your files over from another provider. Log in with your Kinetic details, then enter the login info for your old server in the Transfer tab."
                    },
                    {
                      label: 'Desktop App',
                      title: 'Download the Desktop App',
                      body: 'Connect to any SFTP server and unlock extra features like archiving and NBT file editing.',
                      action: (
                        <a
                          href="https://kinetichosting.com/kinetic-sftp"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="pill-button px-5 py-2.5 text-sm">
                          Download
                        </a>
                      )
                    }
                  ];
                  const slide = slides[Math.min(infoPage, slides.length - 1)];
                  return (
                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="glass-panel rounded-2xl p-8 flex-1 flex flex-col justify-between">
                        <div className="flex flex-col gap-4">
                          <p className="text-xs font-semibold text-textMuted">{slide.label}</p>
                          <h2 className="text-2xl font-semibold text-white m-0">{slide.title}</h2>
                          {slide.body ?
                          <p className="text-sm text-textMuted leading-relaxed">{slide.body}</p> :
                          null}
                          {slide.list ?
                          <ul className="space-y-2 text-sm text-textMuted">
                              {slide.list.map((item) =>
                              <li key={item} className="flex items-start gap-2">
                                  <span className="text-accent mt-0.5">✓</span> {item}
                                </li>
                              )}
                            </ul> :
                          null}
                          {slide.action ? <div className="mt-2">{slide.action}</div> : null}
                        </div>
                        <div className="mt-8 flex items-center justify-between">
                          <button
                            type="button"
                            className="ghost-button px-3 py-2 flex items-center gap-1.5 text-sm disabled:opacity-30"
                            onClick={() => setInfoPage((p) => Math.max(0, p - 1))}
                            disabled={infoPage === 0}>
                            <RiArrowLeftLine /> Prev
                          </button>
                          <div className="flex gap-2">
                            {slides.map((_, i) =>
                            <button
                                key={i}
                                type="button"
                                onClick={() => setInfoPage(i)}
                                className={`h-2 rounded-full transition-all ${i === infoPage ? 'w-6 bg-accent' : 'w-2 bg-border hover:bg-textMuted'}`} />
                            )}
                          </div>
                          <button
                            type="button"
                            className="ghost-button px-3 py-2 flex items-center gap-1.5 text-sm disabled:opacity-30"
                            onClick={() => setInfoPage((p) => Math.min(slides.length - 1, p + 1))}
                            disabled={infoPage === slides.length - 1}>
                            Next <RiArrowRightLine />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

              </div>
            </div>
            }
          </section> :
        showDesktopLoginScreen ?
        <section className="flex-1 min-h-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center py-8 px-4">
            <div className="w-full max-w-md flex flex-col gap-6">
              <div className="flex flex-col items-center gap-3">
                <img src={logoSrc} alt="Kinetic SFTP" className="h-12 w-auto" />
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-white m-0">Connect to your server</h2>
                  <p className="text-sm text-textMuted mt-1">Enter your SFTP or FTPS credentials below.</p>
                </div>
              </div>
              <div className="glass-panel rounded-2xl p-6 flex flex-col gap-3" data-sherman="login-form">
                <div className="grid grid-cols-2 gap-3">
                  <select
                    className="rounded-lg border border-border bg-inset/60 px-3 py-3 text-base text-white focus:border-accent focus:outline-none"
                    value={loginDraft.protocol || 'sftp'}
                    onChange={(e) => {
                      const nextProtocol = e.target.value;
                      setLoginDraft((prev) => {
                        const nextPort = nextProtocol === 'ftps' ? (prev.port === 2022 ? 21 : prev.port || 21) : (prev.port === 21 ? 2022 : prev.port || 2022);
                        return { ...prev, protocol: nextProtocol, port: nextPort };
                      });
                    }}>
                    <option value="sftp">SFTP</option>
                    <option value="ftps">FTPS</option>
                  </select>
                  <input
                    className="rounded-lg border-0 bg-inset/60 px-4 py-3 text-base text-white placeholder:text-textMuted outline-none"
                    placeholder="Port"
                    type="number"
                    value={loginDraft.port}
                    onChange={(e) => setLoginDraft((prev) => ({ ...prev, port: Number(e.target.value) }))} />
                </div>
                <input
                  className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3 text-base text-white placeholder:text-textMuted outline-none"
                  placeholder="Server address"
                  value={loginDraft.host}
                  autoFocus
                  onChange={(e) => {
                    const raw = e.target.value;
                    const parsed = parseHostInput(raw, loginDraft.protocol);
                    setLoginDraft((prev) => ({ ...prev, host: parsed.host || raw, protocol: parsed.host ? parsed.protocol : prev.protocol, port: parsed.port ?? prev.port }));
                  }}
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const parsed = parseHostInput(raw, loginDraft.protocol);
                    if (!parsed.host) return;
                    setLoginDraft((prev) => ({ ...prev, host: parsed.host, protocol: parsed.protocol, port: parsed.port ?? prev.port }));
                  }} />
                <input
                  className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3 text-base text-white placeholder:text-textMuted outline-none"
                  placeholder="Username"
                  value={loginDraft.username}
                  onChange={(e) => setLoginDraft((prev) => ({ ...prev, username: e.target.value }))} />
                <div className="relative">
                  <input
                    className="w-full rounded-lg border-0 bg-inset/60 px-4 py-3 pr-10 text-base text-white placeholder:text-textMuted outline-none"
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginDraft.password}
                    onChange={(e) => setLoginDraft((prev) => ({ ...prev, password: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (connecting || !loginDraft.host || !loginDraft.username || !loginDraft.password) return;
                      handleDraftConnect();
                    }} />
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-textMuted hover:text-white window-no-drag" tabIndex={-1}>
                    {showPassword ? <RiEyeOffLine /> : <RiEyeLine />}
                  </button>
                </div>
                {message && !connecting ?
                  <div key={message} className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">{message}</div> : null}
                <div className="flex flex-col gap-2 mt-2">
                  <button
                    type="button"
                    className="pill-button w-full py-3"
                    onClick={handleDraftConnect}
                    disabled={connecting || !loginDraft.host || !loginDraft.port || !loginDraft.username}>
                    {connecting ? <span className="flex items-center justify-center gap-2"><RiLoader4Line className="animate-spin" /> Connecting...</span> : 'Connect'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button w-full py-2"
                    onClick={handleWebLoginSaveAndConnect}
                    disabled={connecting || !loginDraft.host || !loginDraft.port || !loginDraft.username}>
                    Save & Connect
                  </button>
                  {savedLogins.length > 0 &&
                    <button type="button" className="ghost-button w-full py-2 text-textMuted" onClick={() => setSavedLoginsOpen(true)}>
                      Saved logins ({savedLogins.length})
                    </button>
                  }
                </div>
              </div>
            </div>
          </div>
        </section> :
        <>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2" data-sherman="action-bar">
            {viewMode === 'edit' &&
              <>
                {/* Wide: all buttons inline */}
                {!collapseActionBar && <div className="flex items-center gap-1.5 flex-wrap">
                  {remotePanelActions.map((action) =>
                    <button
                      key={`bar-${action.label}`}
                      type="button"
                      className="ghost-button flex items-center gap-1.5 px-2.5 py-1.5 text-sm"
                      onClick={action.onClick}
                      disabled={action.disabled}>
                      {action.icon}
                      {action.label}
                    </button>
                  )}
                  <button
                    type="button"
                    className="ghost-button flex items-center gap-1.5 px-2.5 py-1.5 text-sm"
                    onClick={() => setShowCheckboxes((v) => !v)}>
                    <RiCheckboxMultipleLine />
                    {showCheckboxes ? 'Hide selection' : 'Show selection'}
                  </button>
                  <button
                    type="button"
                    className="ghost-button flex items-center gap-1.5 px-2.5 py-1.5 text-sm"
                    onClick={() => setShowPanelFiles((v) => !v)}>
                    {showPanelFiles ? <RiEyeOffLine /> : <RiEyeLine />}
                    {showPanelFiles ? 'Hide panel files' : 'Show panel files'}
                  </button>
                </div>}
                {/* Narrow: kebab dropdown */}
                {collapseActionBar && <div className="relative">
                  <button
                    type="button"
                    className="ghost-button p-2"
                    onClick={() => setMobileActionsOpen((v) => !v)}>
                    <RiMore2Line className="text-lg" />
                  </button>
                  {mobileActionsOpen && <>
                    <div className="fixed inset-0 z-30" onClick={() => setMobileActionsOpen(false)} />
                    <div className="absolute top-full left-0 z-40 mt-1 w-52 rounded-xl border border-border bg-inset/95 shadow-xl backdrop-blur py-1">
                      {remotePanelActions.map((action) =>
                        <button
                          key={`mob-${action.label}`}
                          type="button"
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-textPrimary hover:bg-white/5 disabled:opacity-40"
                          onClick={() => { action.onClick(); setMobileActionsOpen(false); }}
                          disabled={action.disabled}>
                          {action.icon}{action.label}
                        </button>
                      )}
                      <div className="my-1 border-t border-border/50" />
                      <button
                        type="button"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-textPrimary hover:bg-white/5"
                        onClick={() => { setShowCheckboxes((v) => !v); setMobileActionsOpen(false); }}>
                        <RiCheckboxMultipleLine />{showCheckboxes ? 'Hide selection' : 'Show selection'}
                      </button>
                      <button
                        type="button"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-textPrimary hover:bg-white/5"
                        onClick={() => { setShowPanelFiles((v) => !v); setMobileActionsOpen(false); }}>
                        {showPanelFiles ? <RiEyeOffLine /> : <RiEyeLine />}{showPanelFiles ? 'Hide panel files' : 'Show panel files'}
                      </button>
                    </div>
                  </>}
                </div>}
              </>
            }
            {!isWebMode && viewMode !== 'edit' && viewMode !== 'settings' &&
              <button
                type="button"
                className="ghost-button px-3 py-1"
                onClick={() => setShowCheckboxes((v) => !v)}>
                {showCheckboxes ? 'Hide selection' : 'Show selection'}
              </button>
            }
          </div>
          <div className="flex items-center gap-2">
            {
              <div className="flex items-center gap-2 bg-inset/60 rounded-lg border border-border p-1" data-sherman="view-tabs">
                {viewTabs.map((mode) =>
                  <button
                    key={mode}
                    type="button"
                    className={`tab-button ${viewMode === mode ? 'tab-button-active' : ''}`}
                    disabled={mode === 'transfer' && !canUseTransfer}
                    onClick={() => {
                      if (mode === 'transfer' && !canUseTransfer) return;
                      setViewMode(mode);
                    }}>
                    {mode === 'normal' ?
                    <><span className="sm:hidden">Files</span><span className="hidden sm:inline">Normal View</span></> :
                    mode === 'edit' ?
                    <><span className="sm:hidden">Files</span><span className="hidden sm:inline">File View</span></> :
                    mode === 'transfer' ?
                    'Transfer' :
                    <><span className="sm:hidden">⚙</span><span className="hidden sm:inline">Settings</span></>}
                  </button>
                )}
              </div>
            }
          </div>
        </div>

        <div className="flex flex-col min-h-0 flex-1 gap-2">
          {viewMode === 'edit' &&
            <MobilePanelBar
              tabs={[
                { id: 'files', label: 'Files' },
                { id: 'editor', label: 'Editor' },
                { id: 'activity', label: 'Activity' },
              ]}
              activeTabId={mobilePanelTab}
              onSelect={setMobilePanelTab} />}
          {viewMode === 'transfer' &&
            <MobilePanelBar
              tabs={[
                { id: 'other', label: 'Other Server' },
                { id: 'host', label: 'Host Server' },
                { id: 'activity', label: 'Activity' },
              ]}
              activeTabId={mobilePanelTab}
              onSelect={setMobilePanelTab} />}
          {viewMode === 'settings' &&
            <MobilePanelBar
              tabs={[
                { id: 'settings', label: 'Settings' },
                { id: 'activity', label: 'Activity' },
              ]}
              activeTabId={mobilePanelTab}
              onSelect={setMobilePanelTab} />}
          <div className="flex-1 min-h-0 flex flex-col" style={{ display: isMobileLayout && mobilePanelTab === 'activity' ? 'none' : '' }}>
            {viewMode === 'normal' ?
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-2" ref={paneContainerRef}>
                <section
                  className="glass-panel rounded-2xl p-4 flex flex-col min-h-0 flex-1"
                  style={isMobileLayout ? { flex: 1, display: mobilePanelTab !== 'local' ? 'none' : '' } : { flexBasis: `${paneSplit * 100}%`, minWidth: '28%' }}>
                  
                <div className="flex flex-col md:flex-row items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-textMuted">Local</p>
                    <p className="text-base text-textPrimary/80 break-all">
                      {canUseLocalFs ?
                      canPickLocalFolder && !hasPickedLocalFolder ?
                      `Choose a local folder to start. ${localFolderHint}` :
                      localPath :
                      localFsUnavailableMessage}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {localPanelActions.map((action) =>
                      <button
                        key={action.label}
                        type="button"
                        onClick={action.onClick}
                        disabled={action.disabled}
                        className="ghost-button flex items-center gap-2">
                        
                        {action.icon}
                        {action.label}
                      </button>
                      )}
                  </div>
                </div>
                <FileList
                    entries={visibleLocalEntries}
                    onOpen={(entry) =>
                    entry.type === 'dir' ? refreshLocal(entry.path) : openEditor(entry, false)
                    }
                    onSelect={(entry, meta) => {
                      setSelectedLocal(entry);
                      setLocalSelectedSet((prev) => {
                        if (meta?.ctrlKey) {
                          const next = new Set(prev);
                          if (next.has(entry.path)) next.delete(entry.path);else
                          next.add(entry.path);
                          return next;
                        }
                        return new Set([entry.path]);
                      });
                    }}
                    selected={selectedLocal}
                    selectedSet={localSelectedSet}
                    onToggleSelect={(entry, checked) => {
                      if (checked) setSelectedLocal(entry);
                      else setSelectedLocal((prev) => prev?.path === entry.path ? null : prev);
                      setLocalSelectedSet((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(entry.path);else
                        next.delete(entry.path);
                        return next;
                      });
                    }}
                    side="local"
                    containerStyle={listContainerStyle}
                    onUp={() => refreshLocal(localParent(localPath))}
                    onContextMenuBlank={(e) =>
                    setContextMenu({ x: e.clientX, y: e.clientY, item: { side: 'local', blank: true } })
                    }
                    openOnClick="dir"
                    showCheckboxes={showCheckboxes} />
                  
              </section>
              <div
                  className="hidden lg:flex items-center justify-center px-1 cursor-col-resize"
                  onMouseDown={(e) => {
                    const container = paneContainerRef.current;
                    if (!container) return;
                    const rect = container.getBoundingClientRect();
                    const startX = e.clientX;
                    const startSplit = paneSplit;
                    const onMove = (ev) => {
                      const delta = ev.clientX - startX;
                      let next = startSplit + delta / rect.width;
                      next = Math.max(0.25, Math.min(0.75, next));
                      setPaneSplit(next);
                    };
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}>
                  
                <div className="h-full w-1 rounded-full bg-border/60" />
              </div>

              <section
                  className="glass-panel rounded-2xl p-4 flex flex-col min-h-0 flex-1 relative"
                  style={isMobileLayout ? { flex: 1, display: mobilePanelTab !== 'remote' ? 'none' : '' } : { flexBasis: `${(1 - paneSplit) * 100}%`, minWidth: '28%' }}>
                {tabSwitchingOverlay &&
                  <div className="absolute inset-0 z-20 rounded-2xl bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <RiLoader4Line className="text-accent text-3xl animate-spin" />
                    <p className="text-sm font-semibold text-white/80">Connecting...</p>
                  </div>
                }

                <div className="flex flex-col md:flex-row items-start justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-textMuted">Remote</p>
                    <p className="text-base text-textPrimary/80" title={remotePath}>{truncatePath(remotePath)}</p>
                    <p className="text-sm text-textMuted">Drag files or folders here to upload.</p>
                  </div>
                </div>
                <FileList
                    entries={visibleRemoteEntries}
                    onOpen={(entry) =>
                    entry.type === 'dir' ? refreshRemote(entry.path) : openEditor(entry, true)
                    }
                    onSelect={(entry, meta) => {
                      setSelectedRemote(entry);
                      setRemoteSelectedSet((prev) => {
                        if (meta?.ctrlKey) {
                          const next = new Set(prev);
                          if (next.has(entry.path)) next.delete(entry.path);else
                          next.add(entry.path);
                          return next;
                        }
                        return new Set([entry.path]);
                      });
                    }}
                    selected={selectedRemote}
                    selectedSet={remoteSelectedSet}
                    onToggleSelect={(entry, checked) => {
                      if (checked) setSelectedRemote(entry);
                      else setSelectedRemote((prev) => prev?.path === entry.path ? null : prev);
                      setRemoteSelectedSet((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(entry.path);else
                        next.delete(entry.path);
                        return next;
                      });
                    }}
                    side="remote"
                    enableDrop={settings.uiux.dragDropBehavior !== 'disabled'}
                    containerStyle={listContainerStyle}
                    onUp={() => refreshRemote(remoteParent(remotePath))}
                    onContextMenu={(e, entry) => {
                      const isInSelection = remoteSelectedSet.has(entry.path) && remoteSelectedSet.size > 1;
                      const items = isInSelection
                        ? remoteEntries.filter((re) => remoteSelectedSet.has(re.path))
                        : [entry];
                      if (!isInSelection) {
                        setSelectedRemote(entry);
                        setRemoteSelectedSet(new Set([entry.path]));
                      }
                      setContextMenu({ x: e.clientX, y: e.clientY, item: entry, items });
                    }}
                    onContextMenuBlank={(e) =>
                    setContextMenu({ x: e.clientX, y: e.clientY, item: { side: 'remote', blank: true } })
                    }
                    showCheckboxes={showCheckboxes}
                    showPanelFiles={showPanelFiles}
                    onPanelFileWarning={(entry, info) => setPanelFileWarning({ entry, info })}
                    onDropFiles={handleRemoteDropFiles}
                    loadingPath={loadingRemotePath}
                    loadingFilePath={loadingFilePath}
                    loadingUp={loadingRemotePath === remoteParent(remotePath)}
                    isRoot={remotePath === '/'}
                    activeTransferCount={activeTransferCount}
                    deletingPaths={deletingPaths}
                    enableEntryDrag
                    dragFrom="remote"
                    currentPath={remotePath}
                    onMoveEntries={handleDragMoveEntries}
                    folderSizeMap={folderSizeMap}
                    folderSizeLoading={folderSizeLoading}
                    onPageChange={(page) => refreshRemote(remotePath, { page })}
                    paginationDisabled={loadingRemote} />

              </section>
              </div> :
              viewMode === 'edit' ?
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-2" ref={paneContainerRef}>
                <section className="glass-panel rounded-2xl p-4 flex flex-col min-h-0 relative" data-sherman="files" style={isMobileLayout ? { flex: 1, display: mobilePanelTab !== 'files' ? 'none' : '' } : { flexBasis: `${paneSplitEdit * 100}%`, minWidth: '240px' }}>
                {(zipInProgress || tabSwitchingOverlay) &&
                  <div className="absolute inset-0 z-20 rounded-2xl bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center gap-3">
                    <RiLoader4Line className="text-accent text-3xl animate-spin" />
                    <p className="text-sm font-semibold text-white/80">{tabSwitchingOverlay ? 'Connecting...' : 'Preparing bundled upload...'}</p>
                  </div>
                }
                <div className="flex items-center justify-between gap-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-textMuted">Remote files</p>
                    <p className="text-base text-textPrimary/80" title={remotePath}>{truncatePath(remotePath)}</p>
                    <p className="text-sm text-textMuted">Drag files or folders here to upload.</p>
                  </div>
                </div>
                <FileList
                    entries={visibleRemoteEntries}
                    onOpen={(entry) =>
                    entry.type === 'dir' ? refreshRemote(entry.path) : openEditor(entry, true)
                    }
                    onSelect={(entry, meta) => {
                      setSelectedRemote(entry);
                      setRemoteSelectedSet((prev) => {
                        if (meta?.ctrlKey) {
                          const next = new Set(prev);
                          if (next.has(entry.path)) next.delete(entry.path);else
                          next.add(entry.path);
                          return next;
                        }
                        return new Set([entry.path]);
                      });
                    }}
                    selected={selectedRemote}
                    selectedSet={remoteSelectedSet}
                    onToggleSelect={(entry, checked) => {
                      if (checked) setSelectedRemote(entry);
                      else setSelectedRemote((prev) => prev?.path === entry.path ? null : prev);
                      setRemoteSelectedSet((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(entry.path);else
                        next.delete(entry.path);
                        return next;
                      });
                    }}
                    side="remote"
                    enableDrop={settings.uiux.dragDropBehavior !== 'disabled'}
                    containerStyle={listContainerStyle}
                    onUp={() => refreshRemote(remoteParent(remotePath))}
                    onContextMenu={(e, entry) => {
                      const isInSelection = remoteSelectedSet.has(entry.path) && remoteSelectedSet.size > 1;
                      const items = isInSelection
                        ? remoteEntries.filter((re) => remoteSelectedSet.has(re.path))
                        : [entry];
                      if (!isInSelection) {
                        setSelectedRemote(entry);
                        setRemoteSelectedSet(new Set([entry.path]));
                      }
                      setContextMenu({ x: e.clientX, y: e.clientY, item: entry, items });
                    }}
                    onContextMenuBlank={(e) =>
                    setContextMenu({ x: e.clientX, y: e.clientY, item: { side: 'remote', blank: true } })
                    }
                    openOnClick
                    showCheckboxes={showCheckboxes}
                    showPanelFiles={showPanelFiles}
                    onPanelFileWarning={(entry, info) => setPanelFileWarning({ entry, info })}
                    onDropFiles={handleRemoteDropFiles}
                    loadingPath={loadingRemotePath}
                    loadingFilePath={loadingFilePath}
                    loadingUp={loadingRemotePath === remoteParent(remotePath)}
                    isRoot={remotePath === '/'}
                    activeTransferCount={activeTransferCount}
                    deletingPaths={deletingPaths}
                    enableEntryDrag
                    dragFrom="remote"
                    currentPath={remotePath}
                    onMoveEntries={handleDragMoveEntries}
                    folderSizeMap={folderSizeMap}
                    folderSizeLoading={folderSizeLoading}
                    onPageChange={(page) => refreshRemote(remotePath, { page })}
                    paginationDisabled={loadingRemote} />

              </section>
              <div
                  className="hidden lg:flex items-center justify-center px-1 cursor-col-resize"
                  onMouseDown={(e) => {
                    const container = paneContainerRef.current;
                    if (!container) return;
                    const rect = container.getBoundingClientRect();
                    const startX = e.clientX;
                    const startSplit = paneSplitEdit;
                    const onMove = (ev) => {
                      const delta = ev.clientX - startX;
                      let next = startSplit + delta / rect.width;
                      next = Math.max(0.2, Math.min(0.8, next));
                      setPaneSplitEdit(next);
                    };
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                    };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  }}>
                  
                <div className="h-full w-1 rounded-full bg-border/60" />
              </div>
              <section className="glass-panel rounded-2xl p-4 flex flex-col min-h-0 min-w-0 flex-1 overflow-hidden" data-sherman="editor" style={{ display: isMobileLayout && mobilePanelTab !== 'editor' ? 'none' : undefined }}>
                <div className="hidden sm:flex items-center justify-between">
                  <div className="space-y-1 min-w-0">
                    <p className="text-sm font-semibold text-textMuted">Editor</p>
                    <p className="text-base text-textPrimary/80 truncate">
                      {editor ? editor.path : 'Open a file to edit'}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {configEditor && !editor?.isLargeFile ?
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className={`ghost-button ${editorView === 'config' ? 'border-accent text-accent' : ''}`}
                          onClick={() => setEditorView('config')}>
                          
                          Config
                        </button>
                        <button
                          type="button"
                          className={`ghost-button ${editorView === 'raw' ? 'border-accent text-accent' : ''}`}
                          onClick={() => setEditorView('raw')}>
                          
                          Raw
                        </button>
                      </div> :
                      null}
                    <button
                        type="button"
                        className="ghost-button"
                        onClick={() => setEditor(null)}
                        disabled={!editor}>
                        
                      Close
                    </button>
                    <button
                        type="button"
                        className="pill-button px-4 py-2"
                        onClick={saveEditor}
                        disabled={!editor || editor?.isImage || editor?.isLargeFile}>

                      Save
                    </button>
                  </div>
                </div>
                <div className="sm:mt-3 flex-1 min-h-0 min-w-0 overflow-hidden">
                  {editor ?
                    editor.isImage ?
                    <div className="h-full w-full rounded-xl border border-border bg-[#11141b] overflow-auto flex items-center justify-center p-4">
                      <img
                        src={editor.imageUrl}
                        alt={editor.path}
                        className="max-w-full max-h-full object-contain rounded"
                        draggable={false} />
                    </div> :
                    editor.isLargeFile ?
                    <LargeFileViewer
                      editor={editor}
                      onNavigateChunk={(offset) => loadLargeFileChunk(offset)}
                      loading={loadingFilePath === editor.path}
                      languageOverride={editorLanguage}
                      onLanguageChange={(lang) => setEditorLanguage(lang)}
                      softWrap={editorWrap}
                      onToggleWrap={() => setEditorWrap((v) => !v)} /> :
                    editorView === 'config' && configEditor ?
                    <ConfigEditor
                      editor={editor}
                      config={configEditor}
                      onChangeContent={(val) => setEditor({ ...editor, content: val })} /> :

                    <CodeEditor
                      value={editor.content}
                      onChange={(val) => setEditor({ ...editor, content: val })}
                      path={editor.path}
                      languageOverride={editorLanguage}
                      onLanguageChange={(lang) => setEditorLanguage(lang)}
                      softWrap={editorWrap}
                      onToggleWrap={() => setEditorWrap((v) => !v)}
                      onSave={saveEditor} /> :

                    <div className="flex h-full items-center justify-center rounded-xl border border-border text-textMuted text-base">
                      Select a file from the remote list to edit
                    </div>
                    }
                </div>
              </section>
            </div> :
              viewMode === 'settings' ?
              <SettingsPanel
                settings={settings}
                updateSetting={updateSetting}
                resetSettings={resetSettings}
                theme={theme}
                themeDraft={themeDraft}
                onThemeChange={handleThemeChange}
                onThemeCommit={handleThemeCommit}
                onThemeReset={handleThemeReset}
                updateStatus={updateStatus}
                checkForUpdates={checkForUpdates}
                handleDownloadUpdate={handleDownloadUpdate}
                canDownloadUpdate={canDownloadUpdate}
                updateAvailable={updateAvailable}
                updateDownloadBlocked={updateDownloadBlocked}
                updateError={updateError}
                latestUpdate={latestUpdate}
                updateCheckedAt={updateCheckedAt}
                APP_VERSION={APP_VERSION}
                formatSize={formatSize}
                isWebMode={isWebMode} /> :


              <TransferView
                transferSplit={transferSplit}
                setTransferSplit={setTransferSplit}
                transferLeftPath={transferLeftPath}
                transferRightPath={transferRightPath}
                connection={connection}
                transferConnecting={transferConnecting.right}
                transferConnected={transferConnected.right}
                handleTransferConnect={handleTransferConnect}
                refreshTransferSide={refreshTransferSide}
                moveBetween={moveBetween}
                onNavigateTransfer={navigateTransferSide}
                transferLeftSelected={transferLeftSelected}
                transferRightSelected={transferRightSelected}
                setTransferLeftSelected={setTransferLeftSelected}
                setTransferRightSelected={setTransferRightSelected}
                transferLeftEntries={transferLeftEntries}
                transferRightEntries={transferRightEntries}
                transferRight={transferRight}
                setTransferRight={setTransferRight}
                listContainerStyle={listContainerStyle}
                visibleTransferLeftEntries={visibleTransferLeftEntries}
                visibleTransferRightEntries={visibleTransferRightEntries}
                showCheckboxes={showCheckboxes}
                remoteParent={remoteParent}
                canUseLocalFs={!isWebMode && canUseLocalFs}
                handleTransferDisconnect={handleTransferDisconnect}
                transferDisconnectDisabled={disconnectBlocked}
                leftConnectingOverlay={tabSwitchingOverlay || transferConnecting.left}
                transferStatusMap={transferStatusMap}
                transferring={transferring}
                transferOverlaySide={transferOverlaySide}
                transferProgress={transferProgress}
                transferLoadingPath={transferLoadingPath}
                transferRefreshing={transferRefreshing}
                transferAllFromOther={transferAllFromOther}
                mobilePanelTab={mobilePanelTab}
                isMobileLayout={isMobileLayout} />

              }
          </div>
          <div
            style={{
              display: isMobileLayout && mobilePanelTab !== 'activity' ? 'none' : '',
              ...(isMobileLayout && mobilePanelTab === 'activity' ? { flex: 1, minHeight: 0 } : { flexShrink: 0 }),
            }}>
            <ActivityPanel
              isMobileLayout={isMobileLayout}
              mobilePanelTab={mobilePanelTab}
              activityHeight={activityHeight}
              setActivityHeight={setActivityHeight}
              transferTab={transferTab}
              setTransferTab={setTransferTab}
              transfers={transfers}
              onCancelTransfer={cancelTransfer}
              onCancelAllTransfers={cancelAllActive}
              onClearTransfers={clearFinished}
              onClearLogs={() => setLogs([])}
              logs={logs} />
          </div>
        </div>
        </>}
      {panelFileWarning &&
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-panel rounded-2xl p-6 max-w-md w-full mx-4 space-y-4">
            <div className="flex items-center gap-3">
              {(() => {
                const PIcon = panelFileWarning.info.Icon;
                return <PIcon className={`${panelFileWarning.info.color} text-2xl`} />;
              })()}
              <h3 className="text-lg font-semibold text-white">{panelFileWarning.info.label}</h3>
            </div>
            <p className="text-sm text-textMuted leading-relaxed">
              This {panelFileWarning.entry.type === 'dir' ? 'folder is' : 'file is'} used by the hosting panel.
              Deleting or modifying files within {panelFileWarning.entry.type === 'dir' ? 'this folder' : 'this file'} could
              cause issues with your server. Proceed with caution.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                className="ghost-button px-4 py-2 text-sm flex-1"
                onClick={() => setPanelFileWarning(null)}>
                Go Back
              </button>
              <button
                type="button"
                className="pill-button px-4 py-2 text-sm flex-1 !bg-red-500/80 hover:!bg-red-500"
                onClick={() => {
                  const entry = panelFileWarning.entry;
                  setPanelFileWarning(null);
                  if (entry.type === 'dir') {
                    refreshRemote(entry.path);
                  } else {
                    openEditor(entry, true);
                  }
                }}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      }
      </main>

      {viewMode === 'normal' ?
        <EditorModal
          editor={editor}
          onClose={() => setEditor(null)}
          onSave={saveEditor}
          onChangeContent={(content) => setEditor({ ...editor, content })}
          languageOverride={editorLanguage}
          onLanguageChange={(lang) => setEditorLanguage(lang)}
          softWrap={editorWrap}
          onToggleWrap={() => setEditorWrap((v) => !v)}
          configEditor={configEditor}
          editorView={editorView}
          onChangeView={setEditorView}
          onNavigateLargeChunk={(offset) => loadLargeFileChunk(offset)}
          loadingLargeFile={loadingFilePath === editor?.path} /> :

        null}
      {canUseNbt && nbtEditor ?
        <NbtEditorModal
          state={nbtEditor}
          onClose={() => setNbtEditor(null)}
          onSave={saveNbtEditor}
          onChangeText={(val) =>
          setNbtEditor((prev) => prev ? { ...prev, text: val, dirty: true } : prev)
          }
          onSelectChunk={(idx) => loadNbtChunk(idx)} /> :

        null}
      {contextMenu ?
        <div
          className="fixed inset-0 z-40"
          onClick={() => setContextMenu(null)}
          onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}>
          
          <div
            className="absolute z-50 w-48 rounded-lg border border-border bg-inset/90 shadow-lg"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onClick={(e) => e.stopPropagation()}>
            
            {contextMenu.item?.blank ?
            <>
                {contextMenu.item.side === 'local' && (!canUseLocalFs || canPickLocalFolder && !hasPickedLocalFolder) ?
              <div className="px-3 py-2 text-xs text-textMuted">{canUseLocalFs ? `Choose a local folder first. ${localFolderHint}` : localFsUnavailableMessage}</div> :
              <>
                    <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-textPrimary hover:bg-white/5 text-left"
                  onClick={() => {
                    contextMenu.item.side === 'local' ? createLocalFile() : createRemoteFile();
                    setContextMenu(null);
                  }}>
                  
                      New file
                    </button>
                    <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-textPrimary hover:bg-white/5 text-left"
                  onClick={() => {
                    contextMenu.item.side === 'local' ? createLocalFolder() : createRemoteFolder();
                    setContextMenu(null);
                  }}>
                  
                      New folder
                    </button>
                  </>}
              </> :

            (() => {
              const multi = contextMenu.items && contextMenu.items.length > 1;
              const items = contextMenu.items || [contextMenu.item];
              const count = items.length;
              return [
                !multi && contextMenu.item.type === 'file' && {
                  label: 'Open',
                  action: () => openEditor(contextMenu.item, true)
                },
                { label: multi ? `Download ${count} items` : 'Download', action: () => {
                  for (const it of items) handleDownload(it);
                }},
                !multi && { label: 'Rename', action: () => renameRemote(contextMenu.item) },
                !multi && { label: 'Move to...', action: () => moveRemote(contextMenu.item) },
                canUseArchive && { label: multi ? `Archive ${count} items` : 'Archive', action: () => archiveRemote(items) },
                !multi && canUseArchive && contextMenu.item.name?.endsWith('.zip') && {
                  label: 'Unarchive',
                  action: () => unarchiveRemote(contextMenu.item)
                },
                !multi && contextMenu.item.type === 'dir' && {
                  label: 'Calculate size',
                  action: () => {
                    setFolderSizeConfirm({ path: contextMenu.item.path, name: contextMenu.item.name });
                  }
                },
                { label: multi ? `Delete ${count} items` : 'Delete', action: () => deleteRemote(items.length === 1 ? items[0] : items) }
              ];
            })().

            filter(Boolean).
            map((opt) =>
            <button
              key={opt.label}
              type="button"
              onClick={() => {
                opt.action();
                setContextMenu(null);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-textPrimary hover:bg-white/5 text-left">

                    {opt.label}
                  </button>
            )
            }
          </div>
        </div> :
        null}
      <FolderSizeConfirmModal
        item={folderSizeConfirm}
        onCancel={() => setFolderSizeConfirm(null)}
        onConfirm={(path, name) => {
          setFolderSizeConfirm(null);
          calculateFolderSize(path, name);
        }} />
      <NewLoginModal
        open={newLoginOpen}
        loginDraft={loginDraft}
        setLoginDraft={setLoginDraft}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        connecting={connecting}
        onCancel={() => setNewLoginOpen(false)}
        onConnect={handleDraftConnect}
        onSaveAndConnect={handleDraftSaveAndConnect} />
      <SaveLoginModal
        modalData={saveModal}
        setModalData={setSaveModal}
        isWebMode={isWebMode}
        canUseVault={canUseVault}
        onClose={() => setSaveModal(null)}
        onConfirm={handleSaveModalConfirm} />
      <SavedLoginsModal
        open={savedLoginsOpen}
        isWebMode={isWebMode}
        canUseVault={canUseVault}
        savedLoginsTab={savedLoginsTab}
        setSavedLoginsTab={setSavedLoginsTab}
        localLogins={localLogins}
        vaultLogins={vaultLogins}
        savedLogins={savedLogins}
        vaultStatus={vaultStatus}
        vaultPassword={vaultPassword}
        setVaultPassword={setVaultPassword}
        vaultUnlocking={vaultUnlocking}
        vaultError={vaultError}
        onVaultUnlock={handleVaultUnlock}
        connectingLoginName={connectingLoginName}
        onConnectLogin={handleConnectSavedLoginFromModal}
        onDeleteLogin={handleDeleteLogin}
        onClose={() => setSavedLoginsOpen(false)} />
      {modal ?
        <InputModal
          title={modal.title}
          description={modal.description}
          value={modal.value}
          setValue={(val) => setModal((m) => ({ ...m, value: val, error: undefined }))}
          onConfirm={(val) => modal.onConfirm(val)}
          onCancel={modal.onCancel}
          confirmLabel={modal.confirmLabel}
          cancelLabel={modal.cancelLabel}
          requireInput={modal.requireInput}
          actions={modal.actions}
          error={modal.error} /> :

        null}
      {showWebLoginScreen && <BuilderSherman isLoginScreen isTransferMode={webTransferMode} />}
      {showDesktopLoginScreen && <BuilderSherman isLoginScreen isDesktopMode />}
      {connected && <BuilderSherman onNavigate={setViewMode} currentPage={viewMode} isTransferMode={webTransferMode} />}
      </div>
    </ErrorBoundary>);

}

export default App;
